import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePortalApiRequest } from '../../../redux/api/portalApi';
import {
  formatSalesApiError,
  getSalesArrayPayload,
  useSalesEndpoints,
} from '../hooks/useSalesApiResource';
import {
  buildConfigurationPreviewModel,
  buildConfigurationCoverage,
  buildBaseStationPreviewLayout,
  buildPumpTablePreviewModel,
  buildPayload,
  buildValueRuleGroupOperations,
  copyValueRuleGroupDraft,
  FIELD_TYPES,
  filterValueRuleGroups,
  findMatchingValueRule,
  findMatchingFieldValue,
  findDeleteBlocker,
  getButtonMaxClicks,
  getFieldValueLabel,
  getFieldValueMode,
  getPreviewSectionFields,
  isCostSummarySection,
  isNumericFieldType,
  isWholeNumberFieldType,
  getNextId,
  getNextRuleGroupId,
  getPumpTableHpColor,
  getSectionsForStation,
  getValueRuleDependencyFilterOptions,
  getValueRuleGroups,
  isButtonFieldType,
  isHyperlinkFieldType,
  normalizeHyperlinkHref,
  normalizers,
  normalizeConfigurationCoverage,
  reconcileResourceMutation,
  sortByOrderThenName,
  validateDraft,
  validateValueRuleGroupDraft,
} from './configurationAdminUtils';
import StationPartsManager from './StationPartsManager';
import PartAutoSelectManager from './PartAutoSelectManager';
import GlobalRulesManager from './GlobalRulesManager';
import PricingFormulasManager from './PricingFormulasManager';
import PumpPreviewValue from './PumpPreviewValue';
import PumpAssignmentMatrix from './PumpAssignmentMatrix';
import FieldValueBulkEditor from './FieldValueBulkEditor';
import { getFieldValueSnapshotConflict } from './fieldValueBulkUtils';
import ValueRuleBulkEditor from './ValueRuleBulkEditor';
import {
  analyzeValueRuleBulkRows,
  getValueRuleBulkScopeRows,
  getValueRuleSnapshotConflict,
} from './valueRuleBulkUtils';
import {
  buildPartAutoPickPreviewModel,
  normalizePartMatchRule,
} from './partAutoSelectUtils';
import {
  normalizeStationPart,
  normalizeStationPartAssignment,
  normalizeStationPartCategory,
} from './stationPartsUtils';
import './ConfigurationAdmin.css';
import usePreviewPumpRows from './usePreviewPumpRows';
import { buildPumpRowModels, getFixedPumpQuantityUpdates } from './pumpRowRules';
import { jockeyFirst } from './pumpRowOrder';
import { buildPumpRowInitialValues } from './pumpRowDefaults';
import { calculateFinalSalesPrice, calculateMarginPercentages, getPreviewPricingInputs, getSetupAmountPreviewFields, withPreviewMultiplier, comparePreviewPrice } from './finalSalesPrice';
import PriceAdjustmentControls from './PriceAdjustmentControls';
import CostSummaryPreview from './CostSummaryPreview';
import { normalizeAdvancedRule } from './advancedConfigRuleUtils';

const EMPTY_DATA = {
  stations: [],
  sections: [],
  subSections: [],
  fields: [],
  fieldValues: [],
  valueRules: [],
};

const RESOURCE_ENDPOINTS = {
  stations: 'masterStationList',
  sections: 'configSections',
  subSections: 'configSubSections',
  fields: 'configFields',
  fieldValues: 'configFieldValues',
  valueRules: 'configValueRules',
};

const RESOURCE_LABELS = {
  stations: 'station',
  sections: 'section',
  subSections: 'subsection',
  fields: 'field',
  fieldValues: 'possible value',
  valueRules: 'value rule',
};

const FIELD_SCOPES = ['Quote', 'Zone'];
const PREVIEW_RESOURCES = ['sections', 'subSections', 'fields', 'fieldValues', 'valueRules'];
let coverageEndpointUnavailable = String(process.env.REACT_APP_SALES_CONFIG_COVERAGE_ENDPOINT_ENABLED || '').toLowerCase() !== 'true';
let dependencyDraftKey = 0;

const buildEmptyValueRuleGroupDraft = (targetStationId, rules, initialFieldValueId = '') => ({
  ruleGroupId: getNextRuleGroupId(rules),
  parentStationId: targetStationId,
  valueBranches: [{
    clientKey: `value-${++dependencyDraftKey}`,
    fieldValueId: initialFieldValueId,
    stationRuleId: null,
    dependencies: [],
    isDisabled: false,
    isDefaultValue: false,
  }],
});

const getResourceParams = (resource, { sectionId, subSectionId, fieldId, stationId }) => ({
  subSections: sectionId ? { sectionid: sectionId } : undefined,
  fields: subSectionId ? { subsectionid: subSectionId } : undefined,
  fieldValues: fieldId ? { configfieldid: fieldId } : undefined,
  // Rule groups contain a station-parent row plus field-value-parent rows.
  // Load this one table in full so both parent types can be assembled together.
  valueRules: undefined,
}[resource]);

const useConfigurationAdminData = ({ stationId, sectionId, subSectionId, fieldId, configurationEnabled = true }) => {
  const endpoints = useSalesEndpoints();
  const request = usePortalApiRequest();
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadedScopes, setLoadedScopes] = useState({});

  const fetchResource = useCallback(async (resource, params) => {
    const endpointKey = RESOURCE_ENDPOINTS[resource];
    const response = await request({ method: 'GET', url: endpoints[endpointKey].base, params });
    return normalizers[resource](getSalesArrayPayload(response));
  }, [endpoints, request]);

  useEffect(() => {
    const nextStage = !loadedScopes.stations
      ? { resource: 'stations', scope: 'all' }
      : !configurationEnabled
        ? null
        : stationId && !loadedScopes.sections
          ? { resource: 'sections', scope: 'all' }
          : sectionId && loadedScopes.subSections !== sectionId
            ? { resource: 'subSections', scope: sectionId }
            : subSectionId && loadedScopes.fields !== subSectionId
              ? { resource: 'fields', scope: subSectionId }
              : fieldId && loadedScopes.fieldValues !== fieldId
                ? { resource: 'fieldValues', scope: fieldId }
                : fieldId && loadedScopes.valueRules !== 'all'
                  ? { resource: 'valueRules', scope: 'all' }
                  : null;

    if (!nextStage) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    fetchResource(
      nextStage.resource,
      getResourceParams(nextStage.resource, { stationId, sectionId, subSectionId, fieldId })
    ).then((rows) => {
      if (cancelled) return;
      setData((current) => ({ ...current, [nextStage.resource]: rows }));
      setLoadedScopes((current) => ({ ...current, [nextStage.resource]: nextStage.scope }));
    }).catch((requestError) => {
      if (cancelled) return;
      setError(formatSalesApiError(requestError, `Unable to load ${RESOURCE_LABELS[nextStage.resource]} data.`));
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [configurationEnabled, fetchResource, fieldId, loadedScopes, sectionId, stationId, subSectionId]);

  const load = useCallback(async () => {
    const resources = !configurationEnabled
      ? ['stations']
      : fieldId
      ? ['fieldValues', 'valueRules']
      : subSectionId
        ? ['fields']
        : sectionId
          ? ['subSections']
          : stationId
            ? ['sections']
            : ['stations'];

    setLoading(true);
    setError('');
    try {
      for (const resource of resources) {
        const rows = await fetchResource(
          resource,
          getResourceParams(resource, { stationId, sectionId, subSectionId, fieldId })
        );
        setData((current) => ({ ...current, [resource]: rows }));
      }
    } catch (requestError) {
      setError(formatSalesApiError(requestError, 'Unable to refresh configuration administration data.'));
    } finally {
      setLoading(false);
    }
  }, [configurationEnabled, fetchResource, fieldId, sectionId, stationId, subSectionId]);

  const mutate = useCallback(async (resource, method, payload) => {
    const endpointKey = RESOURCE_ENDPOINTS[resource];
    // The Sales configuration API binds its documented property names exactly.
    // Field creation, in particular, requires `Id` rather than `ID`.
    let response;
    try {
      response = await request({
        method,
        url: endpoints[endpointKey].base,
        data: payload,
        contract: { requestCase: 'raw' },
        // A retried write can be committed more than once when the first
        // response is lost. Give this API one longer attempt instead.
        requestPolicy: { retry: false, timeoutMs: 20000 },
      });
    } catch (requestError) {
      if (resource === 'valueRules' && String(method).toUpperCase() === 'POST') {
        try {
          const nextRows = await fetchResource(
            resource,
            getResourceParams(resource, { stationId, sectionId, subSectionId, fieldId })
          );
          setData((current) => ({ ...current, [resource]: nextRows }));
          const committedRule = findMatchingValueRule(nextRows, payload);
          if (committedRule) return committedRule;
        } catch {
          // Preserve the original write error when verification is unavailable.
        }
      }
      throw requestError;
    }

    if (resource === 'fieldValues' && Number(payload?.MaxRows) > 0) {
      const nextRows = await fetchResource(
        resource,
        getResourceParams(resource, { stationId, sectionId, subSectionId, fieldId })
      );
      setData((current) => ({ ...current, [resource]: nextRows }));
      return response;
    }

    const canReconcile = reconcileResourceMutation(resource, [], method, payload, response) !== null;

    if (canReconcile) {
      setData((current) => ({
        ...current,
        [resource]: reconcileResourceMutation(
          resource,
          current[resource],
          method,
          payload,
          response
        ),
      }));
    } else {
      const nextRows = await fetchResource(
        resource,
        getResourceParams(resource, { stationId, sectionId, subSectionId, fieldId })
      );
      setData((current) => ({ ...current, [resource]: nextRows }));
    }

    return response;
  }, [endpoints, fetchResource, fieldId, request, sectionId, stationId, subSectionId]);

  const mutateBatch = useCallback(async (resource, operations) => {
    const endpointKey = RESOURCE_ENDPOINTS[resource];
    let completedCount = 0;

    for (const operation of operations) {
      try {
        await request({
          method: operation.method,
          url: endpoints[endpointKey].base,
          data: operation.payload,
          contract: { requestCase: 'raw' },
          requestPolicy: { retry: false, timeoutMs: 20000 },
        });
      } catch (requestError) {
        const canVerifyCommittedPost = operation.method === 'POST'
          && (resource === 'valueRules' || resource === 'fieldValues');
        if (canVerifyCommittedPost) {
          try {
            const committedRows = await fetchResource(
              resource,
              getResourceParams(resource, { stationId, sectionId, subSectionId, fieldId })
            );
            setData((current) => ({ ...current, [resource]: committedRows }));
            const committedRecord = resource === 'valueRules'
              ? findMatchingValueRule(committedRows, operation.payload)
              : findMatchingFieldValue(committedRows, operation.payload);
            if (committedRecord) {
              completedCount += 1;
              continue;
            }
          } catch {
            // Preserve the original write error when verification is unavailable.
          }
        }
        requestError.completedCount = completedCount;
        throw requestError;
      }
      completedCount += 1;
    }

    const nextRows = await fetchResource(
      resource,
      getResourceParams(resource, { stationId, sectionId, subSectionId, fieldId })
    );
    setData((current) => ({ ...current, [resource]: nextRows }));

    return nextRows;
  }, [endpoints, fetchResource, fieldId, request, sectionId, stationId, subSectionId]);

  return { data, loading, error, setError, load, mutate, mutateBatch, fetchResource, loadedScopes };
};

const getRequestStatus = (error) => Number(error?.status || error?.originalStatus || error?.data?.status || 0);

const useConfigurationCoverage = ({ stationId, enabled, fetchResource }) => {
  const endpoints = useSalesEndpoints();
  const request = usePortalApiRequest();
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!stationId) {
      setCoverage(null);
      setError('');
      return null;
    }

    setLoading(true);
    setError('');
    try {
      if (!coverageEndpointUnavailable) {
        try {
          const response = await request({
            method: 'GET',
            url: endpoints.configCoverage.base,
            params: { parentstationid: stationId },
          });
          const nextCoverage = normalizeConfigurationCoverage(response, stationId);
          setCoverage(nextCoverage);
          return nextCoverage;
        } catch (requestError) {
          if (getRequestStatus(requestError) !== 404) throw requestError;
          coverageEndpointUnavailable = true;
        }
      }

      // Temporary compatibility path until the station-scoped aggregate is
      // available in the Sales API. Keep these reads sequential so they use
      // the same low-contention behavior as the rest of the builder.
      const fallbackData = {};
      for (const resource of ['sections', 'subSections', 'fields', 'fieldValues', 'valueRules']) {
        fallbackData[resource] = await fetchResource(resource);
      }
      const nextCoverage = buildConfigurationCoverage(fallbackData, stationId);
      setCoverage(nextCoverage);
      return nextCoverage;
    } catch (requestError) {
      setError(formatSalesApiError(requestError, 'Unable to load rule coverage.'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [endpoints, fetchResource, request, stationId]);

  useEffect(() => {
    if (!stationId) {
      setCoverage(null);
      setError('');
      return;
    }
    if (coverage && coverage.stationId !== stationId) {
      setCoverage(null);
      return;
    }
    if (enabled && coverage?.stationId !== stationId) load();
  }, [coverage, enabled, load, stationId]);

  return { coverage, coverageLoading: loading, coverageError: error, refreshCoverage: load };
};

const AdminBreadcrumbs = ({ items }) => (
  <nav className="config-admin-breadcrumbs" aria-label="Configuration breadcrumb">
    <ol>
      {items.map((item, index) => (
        <li key={`${item.label}-${index}`} className={index === items.length - 1 ? 'active' : ''}>
          {index < items.length - 1 ? (
            <button type="button" onClick={item.onClick}>{item.label}</button>
          ) : (
            <span aria-current="page">{item.label}</span>
          )}
          {index < items.length - 1 && <i className="bi bi-chevron-right" aria-hidden="true"></i>}
        </li>
      ))}
    </ol>
  </nav>
);

const HIERARCHY_STEPS = [
  { label: 'Station' },
  { label: 'Section' },
  { label: 'Subsection' },
  { label: 'Field' },
  { label: 'Values & Rules' },
  { label: 'Parts', view: 'parts' },
  { label: 'Part Auto Select', view: 'part-auto-select' },
];

const HierarchyProgress = ({ currentStep, stationChildEnabled, selectedView, onSelectStationChild }) => (
  <ol className="config-admin-steps" aria-label="Configuration progress">
    {HIERARCHY_STEPS.map(({ label, view }, index) => {
      const isStationChild = Boolean(view);
      const isActive = index === currentStep;
      const isComplete = selectedView ? index === 0 : index < currentStep;
      const className = `${isActive ? 'active' : (isComplete ? 'complete' : '')}${isStationChild && stationChildEnabled ? ' selectable' : ''}`.trim();
      return (
        <li key={label} className={className} aria-current={isActive ? 'step' : undefined}>
          {isStationChild ? (
            <button type="button" disabled={!stationChildEnabled} onClick={() => onSelectStationChild(view)} aria-label={`Open ${label} for the selected station`}>
              <span>{index + 1}</span>{label}
            </button>
          ) : (
            <><span>{index + 1}</span>{label}</>
          )}
        </li>
      );
    })}
  </ol>
);

const PageHeading = ({ title, description, action }) => (
  <div className="config-admin-page-heading">
    <div>
      <h4>{title}</h4>
      {description && <p>{description}</p>}
    </div>
    {action}
  </div>
);

const EmptyState = ({ children }) => <div className="config-admin-empty">{children}</div>;

const CoverageStatus = ({ coverage, level = 'field', loading = false }) => {
  if (loading && !coverage) return <span className="config-admin-coverage is-loading"><Spinner animation="border" size="sm" /> Calculating coverage</span>;
  if (!coverage) return <span className="config-admin-coverage is-muted">Coverage unavailable</span>;

  if (level === 'field') {
    if (coverage.valueCount === 0) {
      return <span className="config-admin-coverage is-hidden"><strong>No possible values defined</strong><small>Implicitly hidden</small></span>;
    }
    if (!coverage.isActive) {
      return <span className="config-admin-coverage is-hidden"><strong>0 of {coverage.valueCount} values covered</strong><small>Implicitly hidden</small></span>;
    }
    return <span className="config-admin-coverage is-active"><strong>{coverage.coveredValueCount} of {coverage.valueCount} values covered</strong><small>{coverage.ruleCount} rule {coverage.ruleCount === 1 ? 'row' : 'rows'} · Active</small></span>;
  }

  if (coverage.fieldCount === 0) {
    return <span className="config-admin-coverage is-muted"><strong>No fields configured</strong><small>No rule coverage to calculate</small></span>;
  }
  return (
    <span className={`config-admin-coverage ${coverage.activeFieldCount > 0 ? 'is-active' : 'is-hidden'}`}>
      <strong>{coverage.activeFieldCount} of {coverage.fieldCount} fields active</strong>
      <small>{coverage.coveredValueCount} of {coverage.valueCount} values covered</small>
    </span>
  );
};

const EditDeleteButtons = ({ label, disabled, onOpen, onEdit, onDelete }) => (
  <div className="config-admin-actions">
    {onOpen && (
      <Button size="sm" variant="primary" onClick={onOpen} aria-label={`Configure ${label}`}>
        Configure <i className="bi bi-arrow-right" aria-hidden="true"></i>
      </Button>
    )}
    <Button size="sm" variant="outline-primary" disabled={disabled} onClick={onEdit} aria-label={`Edit ${label}`}>
      <i className="bi bi-pencil-square" aria-hidden="true"></i> Edit
    </Button>
    <Button size="sm" variant="outline-danger" disabled={disabled} onClick={onDelete} aria-label={`Delete ${label}`}>
      <i className="bi bi-trash" aria-hidden="true"></i> Delete
    </Button>
  </div>
);

const getFieldValueColumnLabel = (fieldType) => {
  if (isNumericFieldType(fieldType)) return 'Allowed number';
  if (isHyperlinkFieldType(fieldType)) return 'Link URL';
  if (isButtonFieldType(fieldType)) return 'Maximum rows';
  return 'Value';
};

const FieldValueDisplay = ({ field, value }) => {
  const valueLabel = isButtonFieldType(field?.fieldType)
    ? String(getButtonMaxClicks(value))
    : getFieldValueLabel(value);
  const isRange = getFieldValueMode(value) === 'range';
  const href = isHyperlinkFieldType(field?.fieldType) ? normalizeHyperlinkHref(value.optionValue) : '';

  if (href) {
    return (
      <>
        <a href={href} target="_blank" rel="noopener noreferrer">
          <i className="bi bi-link-45deg me-1" aria-hidden="true"></i>
          {field.fieldName}
        </a>
        <small className="d-block text-muted">{valueLabel}</small>
      </>
    );
  }

  return (
    <>
      <strong>{valueLabel}</strong>
      {isRange && <small className="d-block text-muted">Inclusive range</small>}
    </>
  );
};

const PreviewValueSelect = ({ field, onValueChange }) => (
  <>
    <Form.Select
      value={field.selectedValueId || ''}
      disabled={field.isDisabled || field.isSelectedValueDisabled}
      isInvalid={Boolean(field.performance?.error)}
      onChange={(event) => onValueChange(field.id, event.target.value)}
    >
      {field.values.map((value) => (
        <option key={value.id} value={value.id} disabled={value.isDisabled}>
          {value.label}
        </option>
      ))}
    </Form.Select>
    {field.performance?.error && (
      <Form.Control.Feedback type="invalid">{field.performance.error}</Form.Control.Feedback>
    )}
  </>
);

const getPreviewRangeMessage = (selectedValue) => {
  const min = selectedValue?.minValue;
  const max = selectedValue?.maxValue;
  if (min !== null && min !== undefined && max !== null && max !== undefined) return `Must be between ${min} and ${max}.`;
  if (min !== null && min !== undefined) return `Must be ${min} or greater.`;
  if (max !== null && max !== undefined) return `Must be ${max} or less.`;
  return '';
};

const PreviewFieldLabel = ({ field, hideRangeIndicator = false }) => {
  const isRange = !hideRangeIndicator
    && !field.performance
    && isNumericFieldType(field.fieldType)
    && field.selectedValue
    && getFieldValueMode(field.selectedValue) === 'range';
  const rangeMessage = isRange ? getPreviewRangeMessage(field.selectedValue) : '';
  const showInlinePerformanceHint = String(field.fieldName || '').trim().toLowerCase() === 'gpm'
    && Boolean(field.performance?.hint);

  return (
    <>
      <Form.Label>
        {field.fieldName}
        {showInlinePerformanceHint && (
          <span className="config-admin-preview-inline-hint">{field.performance.hint}</span>
        )}
      </Form.Label>
      {rangeMessage && <Form.Text className="config-admin-preview-range-hint">{rangeMessage}</Form.Text>}
      {field.performance?.hint && !showInlinePerformanceHint && (
        <Form.Text className="config-admin-preview-range-hint">{field.performance.hint}</Form.Text>
      )}
    </>
  );
};

const clampPreviewRangeInput = (event, selectedValue) => {
  const rawValue = event.target.value;
  if (rawValue === '') return '';

  const numericValue = Number(rawValue);
  if (!Number.isFinite(numericValue)) return getPreviewRangeMessage(selectedValue);

  const min = selectedValue?.minValue;
  const max = selectedValue?.maxValue;
  const hasMin = min !== null && min !== undefined;
  const hasMax = max !== null && max !== undefined;

  if (hasMin && numericValue < min) {
    event.target.value = min;
    return getPreviewRangeMessage(selectedValue);
  }
  if (hasMax && numericValue > max) {
    event.target.value = max;
    return getPreviewRangeMessage(selectedValue);
  }
  return '';
};

const PreviewFieldControl = ({
  field,
  onValueChange,
  onInputChange,
  hideRangeIndicator = false,
  displayValue,
}) => {
  const fieldType = String(field.fieldType || '').trim().toLowerCase();
  const selectedValue = field.selectedValue;
  const selectedLabel = selectedValue?.label || '';
  const controlDisabled = field.isDisabled || field.isSelectedValueDisabled;
  const maxClicks = getButtonMaxClicks(selectedValue);
  const [rangeError, setRangeError] = useState('');
  const [clickCount, setClickCount] = useState(0);

  useEffect(() => {
    setRangeError('');
    setClickCount(0);
  }, [maxClicks, selectedValue?.id]);

  if (displayValue !== undefined) {
    return <Form.Control value={displayValue} disabled readOnly />;
  }

  // Cost-summary multiplier is an editable decimal, not a fixed-value dropdown.
  if (hideRangeIndicator && ['setup multiplier', 'setup multiplier percent'].includes(String(field.fieldName).trim().toLowerCase())) {
    return <Form.Control aria-label="Setup Multiplier Percentage" type="number" step="any" min="0"
      value={field.inputValue ?? selectedValue?.optionValue ?? 1.1}
      disabled={controlDisabled}
      isInvalid={field.inputValue !== undefined && (field.inputValue === '' || Number(field.inputValue) < 0)}
      onChange={event => onInputChange(field.id, event.target.value)} />;
  }

  if (hideRangeIndicator && String(field.fieldName).trim().toLowerCase() === 'setup multiplier amount') {
    return <Form.Control aria-label="Setup Multiplier Amount" value={field.inputValue ?? selectedValue?.optionValue ?? '—'} disabled readOnly />;
  }

  if (field.performance && isNumericFieldType(field.fieldType)) {
    return (
      <Form.Control
        type="number"
        step={isWholeNumberFieldType(field.fieldType) ? '1' : 'any'}
        value={field.performance.inputValue ?? ''}
        readOnly={field.performance.fixedInputValue !== undefined}
        min={field.performance.allowedRanges?.length ? Math.min(...field.performance.allowedRanges.map(range => range.min)) : undefined}
        max={field.performance.allowedRanges?.length ? Math.max(...field.performance.allowedRanges.map(range => range.max)) : undefined}
        disabled={controlDisabled}
        isInvalid={Boolean(field.performance.error)}
        onChange={(event) => onInputChange(field.id, event.target.value)}
      />
    );
  }

  if (fieldType === 'dropdown') {
    return <PreviewValueSelect field={field} onValueChange={onValueChange} />;
  }

  if (fieldType === 'toggle') {
    const checked = ['yes', 'true', '1', 'on'].includes(String(selectedLabel).trim().toLowerCase());
    const toggledValue = field.values.find((value) => {
      const label = String(value.label || '').trim().toLowerCase();
      return checked
        ? ['no', 'false', '0', 'off'].includes(label)
        : ['yes', 'true', '1', 'on'].includes(label);
    });
    return (
      <Form.Check
        type="switch"
        checked={checked}
        disabled={controlDisabled || !toggledValue}
        onChange={() => toggledValue && onValueChange(field.id, toggledValue.id)}
        label={checked ? 'Enabled' : 'Disabled'}
      />
    );
  }

  if (isNumericFieldType(field.fieldType)) {
    const isRange = selectedValue && getFieldValueMode(selectedValue) === 'range';
    if (isRange) {
      return (
        <>
          <Form.Control
            key={`${field.id}-${selectedValue.id}`}
            type="number"
            step={isWholeNumberFieldType(field.fieldType) ? '1' : 'any'}
            min={selectedValue.minValue ?? undefined}
            max={selectedValue.maxValue ?? undefined}
            value={field.inputValue ?? ''}
            disabled={controlDisabled}
            isInvalid={Boolean(rangeError)}
            onChange={(event) => {
              setRangeError('');
              onInputChange(field.id, event.target.value);
            }}
            onBlur={(event) => {
              const nextRangeError = clampPreviewRangeInput(event, selectedValue);
              setRangeError(hideRangeIndicator ? '' : nextRangeError);
              onInputChange(field.id, event.target.value);
            }}
          />
          {rangeError && <Form.Control.Feedback type="invalid">{rangeError}</Form.Control.Feedback>}
        </>
      );
    }
    return <PreviewValueSelect field={field} onValueChange={onValueChange} />;
  }

  if (isHyperlinkFieldType(field.fieldType)) {
    const href = normalizeHyperlinkHref(selectedValue?.optionValue);
    return href ? (
      <p className="mb-0 small">
        <a className="config-admin-preview-link" href={href} target="_blank" rel="noopener noreferrer">
          <i className="bi bi-link-45deg me-1" aria-hidden="true"></i>
          {field.fieldName}
        </a>
      </p>
    ) : (
      <Form.Control value={selectedLabel} disabled />
    );
  }

  if (isButtonFieldType(field.fieldType)) {
    const reachedLimit = clickCount >= maxClicks;
    return (
      <div className="config-admin-preview-button-control">
        <Button
          type="button"
          disabled={controlDisabled || !maxClicks || reachedLimit}
          onClick={() => setClickCount((current) => Math.min(current + 1, maxClicks))}
        >
          {field.fieldName}
        </Button>
        <Form.Text>{clickCount} of {maxClicks} clicks used</Form.Text>
      </div>
    );
  }

  if (fieldType === 'calculated') {
    return <Form.Control value={selectedLabel} disabled />;
  }

  return (
    <Form.Control
      defaultValue={selectedLabel}
      disabled={controlDisabled}
    />
  );
};

const PreviewFieldFormGroup = ({
  field,
  label,
  onValueChange,
  onInputChange,
  hideRangeIndicator = false,
  prefix = '',
  displayValue,
  className = '',
}) => (
  <Form.Group className={`mb-3 config-admin-preview-field ${className}`.trim()}>
    {!isHyperlinkFieldType(field.fieldType) && !isButtonFieldType(field.fieldType) && (
      <PreviewFieldLabel
        field={label ? { ...field, fieldName: label } : field}
        hideRangeIndicator={hideRangeIndicator}
      />
    )}
    <div className={prefix ? 'config-admin-preview-prefixed-control' : undefined}>
      {prefix && <span aria-hidden="true">{prefix}</span>}
      <PreviewFieldControl
        field={field}
        onValueChange={onValueChange}
        onInputChange={onInputChange}
        hideRangeIndicator={hideRangeIndicator}
        displayValue={displayValue}
      />
    </div>
  </Form.Group>
);

const getPreviewFieldColumnSize = (field) => {
  const fieldType = String(field.fieldType || '').trim().toLowerCase();
  if (isHyperlinkFieldType(field.fieldType)) return { md: 12 };
  if (fieldType === 'toggle') return { md: 12, lg: 6 };
  return { md: 6, xl: 4 };
};

const isPreviewStationTypeField = (field) => String(field?.fieldName || '').trim().toLowerCase() === 'station type';
const isPreviewOptionsSection = (section) => [section?.label, section?.sectionKey]
  .some((value) => String(value || '').trim().toLowerCase() === 'options');
const isPreviewPumpConfigurationSection = (section) => [section?.label, section?.sectionKey]
  .some((value) => String(value || '').trim().toLowerCase() === 'pump configuration');
const isPreviewBaseStationSubSection = (subSection) => (
  String(subSection?.subSectionName || '').trim().toLowerCase() === 'base station'
);
const PUMP_CONFIGURATION_COLUMN_ORDER = [
  { fieldName: 'quantity', label: 'QTY', className: 'quantity' },
  { fieldName: 'pump description', label: 'Pump Description', className: 'description' },
  { fieldName: 'motor hp', label: 'Motor HP', className: 'motor-hp' },
  { fieldName: 'pump type', label: 'Type', className: 'type' },
  { fieldName: 'gpm', label: 'GPM', className: 'gpm' },
  { fieldName: 'vfd', label: 'VFD', className: 'option' },
  { fieldName: 'xl', label: 'XL', className: 'option' },
  { fieldName: 'ss', label: 'SS', className: 'option' },
  { fieldName: 'ebv', label: 'EBV', className: 'option' },
];
let previewPumpRowKey = 0;
const createPreviewPumpRow = (buttonField = null, clickNumber = 0, fields = []) => ({
  key: `preview-pump-row-${++previewPumpRowKey}`,
  click: buttonField ? {
    fieldId: buttonField.id,
    fieldValueId: buttonField.selectedValueId,
    number: clickNumber,
    maxClicks: getButtonMaxClicks(buttonField.selectedValue),
  } : null,
  fieldValues: buildPumpRowInitialValues(fields),
});

const OPTION_PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatOptionPrice = (value) => OPTION_PRICE_FORMATTER.format(Number(value) || 0);
const OPTION_AMOUNT_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatOptionAmount = (value) => OPTION_AMOUNT_FORMATTER.format(Number(value) || 0);

const AutoPickPreview = ({ state, model }) => {
  if (!state) return null;
  if (state.status === 'loading') {
    return (
      <div className="config-admin-preview-loading" role="status">
        <Spinner animation="border" size="sm" /> Evaluating Part Auto Select rules...
      </div>
    );
  }
  if (state.status === 'error') return <Alert variant="danger" className="mb-0">{state.error}</Alert>;
  if (state.status !== 'ready' || !model) return null;
  if (model.error) return <Alert variant="danger">{model.error}</Alert>;

  return (
    <div className="config-admin-auto-pick-preview">
      <div className="config-admin-auto-pick-heading">
        <div>
          <h6>Selected Option List</h6>
          <p>Parts matching the current Preview values.</p>
        </div>
        <Badge bg="primary">{model.selectedPartCount} selected</Badge>
      </div>
      {model.rows.length === 0 ? (
        <EmptyState>No assigned parts match the current Preview values.</EmptyState>
      ) : (
        <div className="table-responsive">
          <Table bordered hover size="sm" className="config-admin-auto-pick-table mb-0">
            <thead>
              <tr>
                <th scope="col">Option #</th>
                <th scope="col">Category</th>
                <th scope="col">Part Name</th>
                <th scope="col" className="text-center">QTY</th>
                <th scope="col" className="text-end">List Price</th>
                <th scope="col" className="text-end">Extended Price</th>
              </tr>
            </thead>
            <tbody>
              {model.rows.map((row) => (
                <tr key={row.partId}>
                  <td>{row.partNumber || `Part ${row.partId}`}</td>
                  <td>{row.categoryName}</td>
                  <td>{row.partName}</td>
                  <td className="text-center">{row.quantity ?? '—'}</td>
                  <td className="text-end">{formatOptionPrice(row.listPrice)}</td>
                  <td className="text-end">{formatOptionPrice(row.extendedPrice)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

const AutoPickButton = ({ state, onAutoPick }) => (
  <Button
    type="button"
    variant="primary"
    onClick={onAutoPick}
    disabled={state?.status === 'loading'}
  >
    {state?.status === 'loading'
      ? <Spinner animation="border" size="sm" className="me-2" />
      : <i className="bi bi-magic me-2" aria-hidden="true"></i>}
    Auto Pick
  </Button>
);

const PumpConfigurationPreviewTable = ({
  section,
  stationId,
  sourceData,
  selections,
  inputs,
  onValueChange,
  onInputChange,
  onPumpRowsChange,
}) => {
  const fields = useMemo(() => section.subSections.flatMap((subSection) => [
    ...subSection.fields,
    ...(subSection.disabledFields || []),
  ]), [section.subSections]);
  const buttonFields = useMemo(
    () => fields.filter((field) => isButtonFieldType(field.fieldType)),
    [fields]
  );
  const pumpFields = useMemo(
    () => fields.filter((field) => !isButtonFieldType(field.fieldType)),
    [fields]
  );
  const [pumpRows, setPumpRows] = useState(() => [createPreviewPumpRow(null, 0, pumpFields)]);
  const [testRuleIds, setTestRuleIds] = useState([]);
  const previewRuleData = useMemo(() => sourceData ? { ...sourceData,
    advancedRules: (sourceData.advancedRules || []).map(rule => testRuleIds.includes(rule.Id) ? { ...rule, IsEnabled: true } : rule),
  } : null, [sourceData, testRuleIds]);
  const rowRules = useMemo(() => sourceData ? buildPumpRowModels(
    previewRuleData, stationId, section.id, pumpRows, selections, inputs
  ) : null, [sourceData, previewRuleData, stationId, section.id, pumpRows, selections, inputs]);
  useEffect(() => {
    const updates = rowRules?.advanced?.updates || [];
    const quantityUpdates = getFixedPumpQuantityUpdates(rowRules);
    if (!updates.length && !quantityUpdates.length) return;
    setPumpRows(current => current.map(row => {
      const changes = updates.filter(update => update.rowKey === row.key);
      const quantityChanges = quantityUpdates.filter(update => update.rowKey === row.key);
      if (!changes.length && !quantityChanges.length) return row;
      const fieldValues = { ...row.fieldValues };
      changes.forEach(update => { fieldValues[update.fieldId] = { ...fieldValues[update.fieldId], fieldValueId: update.fieldValueId }; });
      quantityChanges.forEach(update => { fieldValues[update.fieldId] = { ...fieldValues[update.fieldId], inputValue: update.inputValue }; });
      return { ...row, fieldValues };
    }));
  }, [rowRules]);
  const renderedFields = rowRules
    ? [...new Map(rowRules.rows.flatMap(row => row.fields)
      .filter(field => !isButtonFieldType(field.fieldType)).map(field => [field.id, field])).values()]
    : pumpFields;
  const fieldByName = new Map(renderedFields.map((field) => [String(field.fieldName || '').trim().toLowerCase(), field]));
  const configuredNames = new Set(PUMP_CONFIGURATION_COLUMN_ORDER.map((column) => column.fieldName));
  const columns = [
    ...PUMP_CONFIGURATION_COLUMN_ORDER.flatMap((column) => {
      const field = fieldByName.get(column.fieldName);
      return field ? [{ ...column, field }] : [];
    }),
    ...renderedFields
      .filter((field) => !configuredNames.has(String(field.fieldName || '').trim().toLowerCase()))
      .map((field) => ({ field, label: field.fieldName, className: 'additional' })),
  ];

  useEffect(() => {
    setPumpRows([createPreviewPumpRow(null, 0, pumpFields)]);
    // Reset only when the preview changes to a different configured section.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.id]);

  const getButtonClickCount = (fieldId) => pumpRows
    .filter((row) => row.click?.fieldId === fieldId).length;

  const addPumpRow = (buttonField) => {
    const maxClicks = getButtonMaxClicks(buttonField.selectedValue);
    setPumpRows((current) => {
      const clickCount = current.filter((row) => row.click?.fieldId === buttonField.id).length;
      if (!maxClicks || clickCount >= maxClicks) return current;
      return [...current, createPreviewPumpRow(buttonField, clickCount + 1, pumpFields)];
    });
  };

  const updatePumpRow = (rowKey, fieldId, changes) => {
    setPumpRows((current) => current.map((row) => row.key === rowKey ? {
      ...row,
      fieldValues: {
        ...row.fieldValues,
        [fieldId]: {
          ...(row.fieldValues[fieldId] || {}),
          ...changes,
        },
      },
    } : row));
  };

  const getPumpRowField = useCallback((row, field) => {
    if (rowRules) {
      return rowRules.rows.find(candidate => candidate.key === row.key)?.fields.find(candidate => candidate.id === field.id)
        || { ...field, isHidden: true, selectedValue: null, isSelectedValueDisabled: false };
    }
    const rowValue = row.fieldValues[field.id];
    if (!rowValue) return field;
    const selectedValueId = Number(rowValue.fieldValueId) || field.selectedValueId;
    const selectedValue = field.values.find((value) => value.id === selectedValueId) || field.selectedValue;
    return {
      ...field,
      selectedValueId,
      selectedValue,
      inputValue: rowValue.inputValue ?? field.inputValue,
      performance: field.performance ? {
        ...field.performance,
        inputValue: rowValue.inputValue ?? field.performance.inputValue,
      } : null,
    };
  }, [rowRules]);

  const orderedPumpRows = useMemo(() => jockeyFirst(pumpRows, row => (
    rowRules?.rows.find(item => item.key === row.key)?.fields
      .find(field => String(field.fieldName).trim().toLowerCase() === 'pump description')?.selectedValue?.optionValue
  )), [pumpRows, rowRules]);

  useEffect(() => {
    if (!onPumpRowsChange) return;
    const serializedRows = orderedPumpRows.map((row) => {
      const selections = {};
      const inputs = {};
      const rowFields = rowRules?.rows.find(candidate => candidate.key === row.key)?.fields || pumpFields;
      rowFields.forEach((field) => {
        const rowField = getPumpRowField(row, field);
        if (Number(rowField.selectedValueId) > 0) selections[field.id] = Number(rowField.selectedValueId);
        if (!isNumericFieldType(field.fieldType)) return;
        const rawInput = rowField.performance?.inputValue ?? rowField.inputValue;
        const selectedValueNumber = Number(
          rowField.selectedValue?.optionValue
          ?? rowField.selectedValue?.minValue
        );
        const numericInput = rawInput !== '' && rawInput !== null && rawInput !== undefined
          ? Number(rawInput)
          : selectedValueNumber;
        if (Number.isFinite(numericInput)) inputs[field.id] = numericInput;
      });
      return { key: row.key, selections, inputs, isValid: rowRules?.isValid !== false };
    });
    onPumpRowsChange(stationId, serializedRows);
  }, [getPumpRowField, onPumpRowsChange, pumpFields, orderedPumpRows, stationId, rowRules]);

  return (
    <Card className="config-admin-preview-section config-admin-pump-config-preview">
      <Card.Body>
        {(sourceData?.advancedRules || []).some(rule => !rule.IsEnabled) && <Accordion className="mb-3"><Accordion.Item eventKey="test-rules"><Accordion.Header>Test disabled global rules (preview only)</Accordion.Header><Accordion.Body><p>This does not enable or save rules. Automatic changes affect only this preview; reopen the preview to reset them.</p>{sourceData.advancedRules.filter(rule => !rule.IsEnabled).map(rule => <Form.Check key={rule.Id} id={`preview-rule-${stationId}-${rule.Id}`} label={rule.Name} checked={testRuleIds.includes(rule.Id)} onChange={event => setTestRuleIds(current => event.target.checked ? [...current, rule.Id] : current.filter(id => id !== rule.Id))} />)}</Accordion.Body></Accordion.Item></Accordion>}
        {rowRules?.advanced?.errors.map((error, index) => <Alert variant="danger" key={index}>{error.message}</Alert>)}
        {Number.isFinite(rowRules?.capacity) && (
          <div className={`small mb-2 ${rowRules.total > rowRules.capacity ? 'text-danger' : 'text-muted'}`}>
            Main + Jockey: {rowRules.total} of {rowRules.capacity} pumps. Each row must also satisfy its own quantity limit.
          </div>
        )}
        <div className="config-admin-pump-config-heading">
          <h6>{section.label || section.sectionKey}</h6>
          {buttonFields.map((field) => {
            const maxClicks = getButtonMaxClicks(field.selectedValue);
            const clickCount = getButtonClickCount(field.id);
            return (
              <div className="config-admin-pump-row-button" key={field.id}>
                <Button
                  type="button"
                  size="sm"
                  disabled={field.isDisabled || field.isSelectedValueDisabled || !maxClicks || clickCount >= maxClicks}
                  onClick={() => addPumpRow(field)}
                >
                  <i className="bi bi-plus-circle" aria-hidden="true"></i> {field.fieldName}
                </Button>
                <small>{clickCount} of {maxClicks} clicks used</small>
              </div>
            );
          })}
        </div>
        <div className="table-responsive config-admin-pump-config-scroll">
          <Table bordered className="config-admin-pump-config-table mb-0">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th scope="col" className={`pump-column-${column.className}`} key={column.field.id}>
                    <span>{column.label}</span>
                    {column.field.performance?.hint && <small>{column.field.performance.hint}</small>}
                  </th>
                ))}
                <th scope="col" className="pump-column-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {orderedPumpRows.map((row, rowIndex) => (
                <tr key={row.key}>
                  {columns.map((column) => {
                    const rowField = getPumpRowField(row, column.field);
                    return (
                      <td className={`pump-column-${column.className}`} key={column.field.id}>
                        <PumpPreviewValue field={rowField}>
                        <Form.Group className="config-admin-preview-field config-admin-pump-config-field">
                          <PreviewFieldControl
                            field={rowField}
                            onValueChange={(fieldId, valueId) => {
                              updatePumpRow(row.key, fieldId, { fieldValueId: Number(valueId), inputValue: undefined });
                              // This callback only synchronizes the parent preview/matrix.
                              // Every row is independently evaluated by buildPumpRowModels.
                              if (row.key === pumpRows[0]?.key) onValueChange?.(fieldId, valueId);
                            }}
                            onInputChange={(fieldId, inputValue) => {
                              updatePumpRow(row.key, fieldId, { inputValue });
                              if (row.key === pumpRows[0]?.key) onInputChange?.(fieldId, inputValue);
                            }}
                          />
                          {rowField.performance?.hint && <small className="d-block text-muted">{rowField.performance.hint}</small>}
                        </Form.Group>
                        </PumpPreviewValue>
                      </td>
                    );
                  })}
                  <td className="pump-column-action">
                    <Button
                      type="button"
                      variant="link"
                      disabled={!row.click}
                      onClick={() => setPumpRows((current) => current.filter((candidate) => candidate.key !== row.key))}
                      aria-label={!row.click ? 'The original pump row cannot be removed' : `Remove pump row ${rowIndex + 1}`}
                    >
                      <i className="bi bi-trash" aria-hidden="true"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

const BaseStationPreviewSubSection = ({
  subSection,
  onValueChange,
  onInputChange,
  autoPickExtendedPriceTotal,
}) => {
  const layout = buildBaseStationPreviewLayout(getSetupAmountPreviewFields(subSection));

  return (
    <div className="config-admin-preview-subsection config-admin-cost-base-station">
      <div className="config-admin-preview-subsection-title config-admin-cost-base-title">
        <strong>{subSection.subSectionName}</strong>
      </div>
      <div className="config-admin-cost-base-grid">
        {layout.map((item) => item.type === 'pair' ? (
          <div className="config-admin-cost-base-pair" key={item.key}>
            <div className="config-admin-cost-base-pair-title">{item.label}:</div>
            <div className="config-admin-cost-base-pair-fields">
              <PreviewFieldFormGroup
                field={item.percentageField}
                label="Percentage"
                onValueChange={onValueChange}
                onInputChange={onInputChange}
                hideRangeIndicator
              />
              <PreviewFieldFormGroup
                field={item.amountField}
                label="Amount"
                prefix="$"
                onValueChange={onValueChange}
                onInputChange={onInputChange}
                hideRangeIndicator
              />
            </div>
          </div>
        ) : (
          <div className={item.fullWidth ? 'config-admin-cost-base-field is-full-width' : 'config-admin-cost-base-field'} key={item.key}>
            <PreviewFieldFormGroup
              field={item.field}
              label={item.label}
              prefix={item.prefix}
              displayValue={String(item.field.fieldName || '').trim().toLowerCase() === 'list price'
                ? (autoPickExtendedPriceTotal === undefined
                  ? undefined
                  : formatOptionAmount(autoPickExtendedPriceTotal))
                : undefined}
              onValueChange={onValueChange}
              onInputChange={onInputChange}
              hideRangeIndicator
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const ConfigurationPreviewSubSection = ({
  section,
  subSection,
  onValueChange,
  onInputChange,
  autoPickExtendedPriceTotal,
  finalSalesPrice,
}) => {
  if (isCostSummarySection(section) && isPreviewBaseStationSubSection(subSection)) {
    return (
      <BaseStationPreviewSubSection
        subSection={subSection}
        onValueChange={onValueChange}
        onInputChange={onInputChange}
        autoPickExtendedPriceTotal={autoPickExtendedPriceTotal}
      />
    );
  }

  return (
    <div className="config-admin-preview-subsection">
      <div className="config-admin-preview-subsection-title">
        <strong>{subSection.subSectionName}</strong>
      </div>
      <Row>
        {getPreviewSectionFields(subSection).map((field) => (
          <Col {...getPreviewFieldColumnSize(field)} key={field.id}>
            <PreviewFieldFormGroup
              field={field}
              onValueChange={onValueChange}
              onInputChange={onInputChange}
              hideRangeIndicator={isCostSummarySection(section)}
              displayValue={isCostSummarySection(section) && String(field.fieldName).trim().toLowerCase() === 'final sales price'
                ? (finalSalesPrice?.value == null ? '—' : formatOptionPrice(finalSalesPrice.value)) : undefined}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};

const ConfigurationPreviewPanel = ({
  model,
  sourceData,
  title,
  description,
  emptyMessage,
  stationId,
  stationName,
  stationOptions = [],
  stationSelectable = false,
  onStationChange,
  onValueChange,
  onInputChange,
  autoPickState,
  autoPickModel,
  onAutoPick,
  pumpTableModel,
  onPumpCellOpen,
  onPumpRowsChange,
}) => {
  const [priceAdjustment, setPriceAdjustment] = useState(null);
  const pricingContext = JSON.stringify([model?.stationId, sourceData?.pricingFormulas]);
  useEffect(() => { setPriceAdjustment(null); }, [pricingContext]);
  const multiplierOverride = priceAdjustment?.context === pricingContext ? priceAdjustment.multiplier : null;
  if (!model) return null;
  const pricingInputs = {
    formulas: withPreviewMultiplier(sourceData?.pricingFormulas || [], model.stationId, multiplierOverride), stationId: model.stationId, stationName,
    autoPickTotal: autoPickState?.status === 'ready' && !autoPickModel?.error ? autoPickModel?.extendedPriceTotal : undefined,
    ...getPreviewPricingInputs(model),
  };
  const finalSalesPrice = calculateFinalSalesPrice(pricingInputs);
  const margin = calculateMarginPercentages(pricingInputs);
  const adjustPrice = multiplier => setPriceAdjustment(multiplier == null ? null : { context: pricingContext, multiplier });
  const hasSections = model.sections.length > 0;
  const previewStationName = stationName || `Station ${model.stationId}`;
  const visibleSections = sortByOrderThenName(model.sections, 'sectionKey')
    .map((section) => ({
      ...section,
      subSections: sortByOrderThenName(section.subSections, 'subSectionName')
        .map((subSection) => ({
          ...subSection,
          fields: subSection.fields.filter((field) => !isPreviewStationTypeField(field)),
          disabledFields: (subSection.disabledFields || []).filter((field) => !isPreviewStationTypeField(field)),
        }))
        .filter((subSection) => (
          subSection.fields.length > 0
          || subSection.disabledFields.length > 0
        )),
    }))
    .filter((section) => (
      section.subSections.length > 0
      || (Boolean(onAutoPick) && isPreviewOptionsSection(section))
    ));
  const hasVisibleFields = visibleSections.length > 0;
  const hasOptionsSection = visibleSections.some(isPreviewOptionsSection);
  const pumpTableFieldIds = pumpTableModel?.fieldIds || pumpTableModel?.pumpTypeTables?.[0]?.fieldIds || {};
  const motorHpFieldId = model.performance?.fieldIds?.motorHp || pumpTableFieldIds.motorHp;
  const handlePumpTableHpSelect = motorHpFieldId && onValueChange
    ? (hpValueId) => onValueChange(motorHpFieldId, hpValueId)
    : undefined;
  const handlePumpTablePumpTypeSelect = pumpTableModel?.pumpTypeFieldId && onValueChange
    ? (pumpTypeValueId) => onValueChange(pumpTableModel.pumpTypeFieldId, pumpTypeValueId)
    : undefined;

  return (
    <div className="config-admin-preview-panel">
      <div className="config-admin-preview-heading">
        <div>
          <h5>{title}</h5>
          {description && <p>{description}</p>}
        </div>
      </div>

      {!hasSections ? (
        <EmptyState>{emptyMessage || 'No section configuration is available to preview.'}</EmptyState>
      ) : (
        <Form className="config-admin-preview-form" onSubmit={(event) => event.preventDefault()}>
          <Accordion className="config-admin-preview-pump-accordion">
            <Accordion.Item eventKey="pump-table">
              <Accordion.Header>Pump Table</Accordion.Header>
              <Accordion.Body>
                <PumpTablePreview
                  model={pumpTableModel}
                  stationName={previewStationName}
                  performance={model.performance}
                  previewInputs={model.effectiveInputs}
                  previewSelections={model.effectiveSelections}
                  formPreview
                  selectedPumpTypeValueId={model.effectiveSelections?.[pumpTableModel?.pumpTypeFieldId]}
                  onCellOpen={onPumpCellOpen}
                  onHpSelect={handlePumpTableHpSelect}
                  onPumpTypeSelect={handlePumpTablePumpTypeSelect}
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          <Card className="config-admin-preview-section config-admin-preview-station-card">
            <Card.Body>
              <h6>Station Model</h6>
              <Row>
                <Col md={6} xl={4}>
                  <Form.Group className="mb-3 config-admin-preview-field">
                    <Form.Label>Station Type</Form.Label>
                    <Form.Select
                      value={stationSelectable ? (stationId || model.stationId || '') : previewStationName}
                      disabled={!stationSelectable}
                      onChange={(event) => onStationChange?.(Number(event.target.value))}
                    >
                      {stationSelectable ? stationOptions.map((station) => (
                        <option key={station.id} value={station.id}>{station.stationName}</option>
                      )) : <option value={previewStationName}>{previewStationName}</option>}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {!hasVisibleFields ? (
            <EmptyState>{emptyMessage || 'No fields are active for the current station and rule selections.'}</EmptyState>
          ) : visibleSections.map((section) => (
            isPreviewPumpConfigurationSection(section) ? (
              <PumpConfigurationPreviewTable
                key={`${model.stationId}-${section.id}`}
                section={section}
                stationId={model.stationId}
                sourceData={sourceData}
                selections={model.effectiveSelections}
                inputs={model.effectiveInputs}
                onValueChange={onValueChange}
                onInputChange={onInputChange}
                onPumpRowsChange={onPumpRowsChange}
              />
            ) : isCostSummarySection(section) ? <CostSummaryPreview
              key={section.id} section={section} margin={margin} finalSalesPrice={finalSalesPrice} pricingError={sourceData?.pricingError}
              adjustmentControls={<PriceAdjustmentControls key={pricingContext}
                multiplier={margin.multiplier} disabled={Boolean(finalSalesPrice.error || margin.error || sourceData?.pricingError)}
                adjusted={multiplierOverride != null} finalPrice={finalSalesPrice.value} onAdjust={adjustPrice}
                onCompare={target => adjustPrice(comparePreviewPrice(pricingInputs, target))}
              />}
              renderSubSection={subSection => <ConfigurationPreviewSubSection
                section={section} subSection={subSection} onValueChange={onValueChange} onInputChange={onInputChange}
                autoPickExtendedPriceTotal={onAutoPick ? (autoPickModel?.extendedPriceTotal ?? '') : undefined}
                finalSalesPrice={finalSalesPrice}
              />}
            /> : <Card className="config-admin-preview-section" key={section.id}>
              <Card.Body>
                <div className="config-admin-preview-section-heading">
                  <h6>{section.label || section.sectionKey}</h6>
                  {isPreviewOptionsSection(section) && onAutoPick && (
                    <AutoPickButton state={autoPickState} onAutoPick={onAutoPick} />
                  )}
                </div>
                {section.subSections.map((subSection) => (
                  <ConfigurationPreviewSubSection
                    key={subSection.id}
                    section={section}
                    subSection={subSection}
                    onValueChange={onValueChange}
                    onInputChange={onInputChange}
                    autoPickExtendedPriceTotal={onAutoPick
                      ? (autoPickModel?.extendedPriceTotal ?? '')
                      : undefined}
                    finalSalesPrice={finalSalesPrice}
                  />
                ))}
                {isPreviewOptionsSection(section) && (
                  <AutoPickPreview state={autoPickState} model={autoPickModel} />
                )}
              </Card.Body>
            </Card>
          ))}

          {onAutoPick && !hasOptionsSection && (
            <Card className="config-admin-preview-section">
              <Card.Body>
                <div className="config-admin-preview-section-heading">
                  <h6>Options</h6>
                  <AutoPickButton state={autoPickState} onAutoPick={onAutoPick} />
                </div>
                <AutoPickPreview state={autoPickState} model={autoPickModel} />
              </Card.Body>
            </Card>
          )}

          {model.performance && !model.performance.isValid && (
            <Alert variant="danger" className="mb-0">
              Discharge PSI, GPM, and Motor HP must form a valid combination before this configuration can be submitted.
            </Alert>
          )}
        </Form>
      )}
    </div>
  );
};

const getPumpTableCellAppearance = (label) => {
  const values = String(label || '').split('/').map((value) => Number(value.trim())).filter(Number.isFinite);
  if (values.length > 1) return { className: 'hp-multiple' };
  if (values.length !== 1) return { className: '' };
  return {
    className: 'hp-value',
    style: { backgroundColor: getPumpTableHpColor(values[0]) },
  };
};

const numberWithinPumpBucket = (value, bucket) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= Number(bucket.min) && numericValue <= Number(bucket.max);
};

const getPumpTableSelectionFromPerformance = (activeModel, performance) => {
  if (!activeModel || !performance?.fieldIds) return null;
  const psiInput = performance.adjustedDischargePsi ?? performance.inputValues?.[performance.fieldIds.dischargePsi];
  const gpmInput = performance.inputValues?.[performance.fieldIds.gpm];
  return getPumpTableSelectionFromInputs(activeModel, psiInput, gpmInput);
};

const getPumpTableSelectionFromInputs = (activeModel, psiInput, gpmInput) => {
  if (!activeModel) return null;
  const selectedRow = activeModel.rows.find((row) => numberWithinPumpBucket(psiInput, row));
  const selectedColumn = activeModel.columns.find((column) => numberWithinPumpBucket(gpmInput, column));
  if (!selectedRow && !selectedColumn) return null;
  return {
    pumpTypeValueId: activeModel.pumpTypeValueId || 0,
    rowIndex: selectedRow?.rowIndex ?? -1,
    columnIndex: selectedColumn?.columnIndex ?? -1,
    rowLabel: selectedRow?.label || '',
    columnLabel: selectedColumn?.label || '',
  };
};

export const PumpTablePreview = ({
  model,
  stationName,
  performance,
  previewInputs = {},
  previewSelections = {},
  formPreview = false,
  selectedPumpTypeValueId,
  onHpSelect,
  onPumpTypeSelect,
  onCellOpen,
  cellOpenHint,
  renderHpIndicator,
}) => {
  const [activePumpTypeValueId, setActivePumpTypeValueId] = useState('');
  const [clickedSelection, setClickedSelection] = useState(null);
  const pumpTypeTables = useMemo(() => model?.pumpTypeTables || [], [model]);

  useEffect(() => {
    setActivePumpTypeValueId((current) => (
      pumpTypeTables.some((table) => String(table.pumpTypeValueId) === String(selectedPumpTypeValueId))
        ? String(selectedPumpTypeValueId)
        : pumpTypeTables.some((table) => String(table.pumpTypeValueId) === String(current))
          ? current
          : String(pumpTypeTables[0]?.pumpTypeValueId || '')
    ));
  }, [pumpTypeTables, selectedPumpTypeValueId]);

  const activeModel = pumpTypeTables.find((table) => (
    String(table.pumpTypeValueId) === String(activePumpTypeValueId)
  )) || pumpTypeTables[0] || model;
  const fieldIds = performance?.fieldIds || activeModel?.fieldIds || model?.fieldIds || {};
  const fallbackSelection = getPumpTableSelectionFromInputs(
    activeModel,
    previewInputs[fieldIds.dischargePsi] ?? activeModel?.defaultInputs?.[fieldIds.dischargePsi],
    previewInputs[fieldIds.gpm] ?? activeModel?.defaultInputs?.[fieldIds.gpm]
  );
  const performanceSelection = getPumpTableSelectionFromPerformance(activeModel, performance);
  const activeSelection = formPreview ? (performanceSelection || fallbackSelection) : clickedSelection;
  const selectedRowIndex = Number(activeSelection?.rowIndex);
  const selectedColumnIndex = Number(activeSelection?.columnIndex);
  const hasSelectedRow = Number.isInteger(selectedRowIndex) && selectedRowIndex >= 0;
  const hasSelectedColumn = Number.isInteger(selectedColumnIndex) && selectedColumnIndex >= 0;
  const canSelectHp = typeof onHpSelect === 'function';
  const selectedHpValueId = Number(
    performance?.selectedHpValueId
    || previewSelections[fieldIds.motorHp]
    || activeModel?.defaultHpValueId
    || 0
  );

  useEffect(() => {
    setClickedSelection(null);
  }, [activeModel?.pumpTypeValueId]);


  if (!activeModel?.hasRequiredFields) {
    return <EmptyState>Discharge PSI, GPM, and Motor HP fields are required to build this table.</EmptyState>;
  }
  if (activeModel.rows.length === 0 || activeModel.columns.length === 0) {
    return <EmptyState>No numeric PSI or GPM values are available for this station.</EmptyState>;
  }

  return (
    <>
      {pumpTypeTables.length > 0 && (
        <div className="config-admin-pump-type-tabs" role="tablist" aria-label={`${stationName} pump type tables`}>
          {pumpTypeTables.map((table) => {
            const isActive = table.pumpTypeValueId === activeModel.pumpTypeValueId;
            return (
              <button
                key={table.pumpTypeValueId}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={isActive ? 'active' : ''}
                onClick={() => {
                  setActivePumpTypeValueId(String(table.pumpTypeValueId));
                  onPumpTypeSelect?.(table.pumpTypeValueId);
                }}
              >
                {table.pumpTypeLabel}
              </button>
            );
          })}
        </div>
      )}
      <div role={pumpTypeTables.length > 0 ? 'tabpanel' : undefined}>
      <div className="config-admin-pump-table-summary">
        <span><strong>{stationName}</strong>{activeModel.pumpTypeLabel ? ` — ${activeModel.pumpTypeLabel}` : ''} pump selection matrix</span>
        <span>{activeModel.matchedCellCount} of {activeModel.totalCellCount} cells have complete rules</span>
      </div>
      <div className="config-admin-pump-table-scroll">
        <p className="small text-muted">Click a cell to highlight left to its PSI header and up to its GPM header.{onCellOpen && (cellOpenHint || ' Double-click an HP cell to edit its applicable rules in a modal. Keyboard: Space highlights; Enter opens rules.')}</p>
        <table className="config-admin-pump-table">
          <caption className="visually-hidden">Pump table preview for {stationName}{activeModel.pumpTypeLabel ? `, ${activeModel.pumpTypeLabel}` : ''}</caption>
          <thead>
            <tr>
              <th className="config-admin-pump-table-corner" aria-hidden="true"></th>
              <th colSpan={activeModel.columns.length} className="config-admin-pump-table-gpm-heading">GPM</th>
            </tr>
            <tr>
              <th scope="col" className="config-admin-pump-table-psi-heading">PSI</th>
              {activeModel.columns.map((column) => {
                const isSelectedColumn = hasSelectedColumn && selectedColumnIndex === Number(column.columnIndex);
                return (
                  <th
                    scope="col"
                    key={column.label}
                    className={isSelectedColumn ? 'is-selected-column' : undefined}
                  >
                    {column.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {activeModel.rows.map((row) => (
              <tr key={row.label}>
                <th scope="row" className={hasSelectedRow && selectedRowIndex === Number(row.rowIndex) ? 'is-selected-row' : undefined}>{row.label}</th>
                {row.cells.map((cell) => {
                  const appearance = getPumpTableCellAppearance(cell.label);
                  const rowIndex = Number(row.rowIndex);
                  const columnIndex = Number(cell.columnIndex);
                  const navigable = Boolean(onCellOpen && cell.ruleTargets?.length);
                  const openRules = () => onCellOpen(cell, `${row.label} PSI / ${cell.gpmLabel} GPM${activeModel.pumpTypeLabel ? ` / ${activeModel.pumpTypeLabel}` : ''}`, activeModel);
                  const isSelectedCell = hasSelectedRow && hasSelectedColumn
                    && selectedRowIndex === rowIndex
                    && selectedColumnIndex === columnIndex;
                  const isSelectedRow = hasSelectedRow && hasSelectedColumn
                    && selectedRowIndex === rowIndex
                    && columnIndex <= selectedColumnIndex;
                  const isSelectedColumn = hasSelectedRow && hasSelectedColumn
                    && selectedColumnIndex === columnIndex
                    && rowIndex <= selectedRowIndex;
                  const hpValues = cell.hpValues?.length
                    ? cell.hpValues
                    : cell.hpValueIds.map((id) => ({ id, label: String(id) }));
                  const cellClassName = [
                    appearance.className,
                    navigable ? 'config-admin-pump-rule-link' : '',
                    isSelectedRow ? 'is-selected-row' : '',
                    isSelectedColumn ? 'is-selected-column' : '',
                    isSelectedCell ? 'is-selected-cell' : '',
                    !performance ? 'is-highlightable' : '',
                    canSelectHp && hpValues.length > 0 ? 'is-hp-selectable' : '',
                  ].filter(Boolean).join(' ');
                  const handleCellHighlight = () => {
                    if (performance) return;
                    setClickedSelection({
                      pumpTypeValueId: activeModel.pumpTypeValueId || 0,
                      rowIndex: row.rowIndex,
                      columnIndex: cell.columnIndex,
                      rowLabel: row.label,
                      columnLabel: cell.gpmLabel,
                    });
                  };
                  const handleCellSelect = () => {
                    if (canSelectHp && hpValues.length > 0) {
                      onHpSelect(hpValues[0].id);
                      return;
                    }
                    handleCellHighlight();
                  };
                  const renderHpValue = (hpValue, index) => (
                    <React.Fragment key={hpValue.id}>
                      {index > 0 && <span className="config-admin-pump-table-hp-separator">/</span>}
                      {canSelectHp ? (
                        <button
                          type="button"
                          className={Number(hpValue.id) === selectedHpValueId ? 'config-admin-pump-table-hp-button active' : 'config-admin-pump-table-hp-button'}
                          onClick={(event) => {
                            event.stopPropagation();
                            onHpSelect(hpValue.id);
                          }}
                          aria-label={`Select ${hpValue.label} HP`}
                        >
                          {hpValue.label}
                        </button>
                      ) : (
                        <span className="config-admin-pump-table-hp-text">{hpValue.label}</span>
                      )}
                      {renderHpIndicator?.(cell, hpValue, activeModel)}
                    </React.Fragment>
                  );
                  return (
                    <td
                      key={cell.key}
                      className={cellClassName}
                      style={appearance.style}
                      aria-label={cell.label ? `${cell.label} HP` : 'No matching HP rule'}
                      title={navigable ? 'Double-click to edit applicable rules' : undefined}
                      onDoubleClick={navigable ? openRules : undefined}
                      onClick={handleCellSelect}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === 'Enter' && navigable) {
                          event.preventDefault();
                          openRules();
                          return;
                        }
                        if (!['Enter', ' '].includes(event.key)) return;
                        event.preventDefault();
                        handleCellSelect();
                      }}
                      role={!performance || (canSelectHp && hpValues.length > 0) ? 'button' : undefined}
                      tabIndex={navigable || !performance || (canSelectHp && hpValues.length > 0) ? 0 : undefined}
                    >
                      {hpValues.length > 0 ? hpValues.map(renderHpValue) : '\u00a0'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {activeModel.matchedCellCount === 0 && (
        <Alert variant="secondary" className="mt-3 mb-0">No complete PSI, GPM, and Motor HP rules match this station.</Alert>
      )}
      </div>
    </>
  );
};

export const PumpCellRuleOverview = ({ pump, fieldId, groupKey, saving, onSelect, onCopy, onDelete }) => {
  const valuesById = new Map(pump.source.fieldValues.map((value) => [value.id, value]));
  const fieldsById = new Map(pump.source.fields.map((field) => [field.id, field]));
  return (
    <section className="mb-3" aria-label="Applicable pump rules">
      <h6>All applicable rules</h6>
      <p className="small text-muted">Each row is an alternative rule for this cell. All listed conditions in a row must be met. Edit a group below to change its values and dependencies.</p>
      <Table responsive bordered size="sm">
        <thead><tr><th scope="col">Field</th><th scope="col">Value</th><th scope="col">Group</th><th scope="col">Conditions</th><th scope="col">Action</th></tr></thead>
        <tbody>
          {pump.options.flatMap((option) => option.group.values
            .filter((branch) => option.target.branchKeys.includes(branch.key))
            .map((branch) => {
              const value = valuesById.get(branch.fieldValueId);
              const selected = option.target.fieldId === fieldId && option.group.key === groupKey;
              const conditions = new Map();
              branch.dependencies.forEach((dependency) => {
                const parentValue = valuesById.get(dependency.parentFieldValueId);
                const parentField = fieldsById.get(parentValue?.configFieldId);
                const key = parentField?.id ?? 'unknown';
                if (!conditions.has(key)) conditions.set(key, { label: parentField?.fieldName || 'Value', values: [] });
                conditions.get(key).values.push(parentValue ? getFieldValueLabel(parentValue) : String(dependency.parentFieldValueId));
              });
              return (
                <tr key={option.target.fieldId + ':' + branch.key} className={selected ? 'table-primary' : undefined}>
                  <td>{option.target.fieldName}</td>
                  <td>{value ? getFieldValueLabel(value) : branch.fieldValueId}</td>
                  <td>{option.group.ruleGroupId}</td>
                  <td>{conditions.size ? Array.from(conditions.entries()).map(([key, condition]) => (
                    <div key={key}><strong>{condition.label}:</strong> {condition.values.join(' OR ')}</div>
                  )) : 'No additional conditions'}</td>
                  <td><div className="d-flex flex-column gap-1"><Button type="button" size="sm" variant="outline-primary" disabled={saving || selected} onClick={() => onSelect(option)}
                    aria-label={'Edit ' + option.target.fieldName + ' group ' + option.group.ruleGroupId}>
                    {selected ? 'Editing' : 'Edit'}
                  </Button>
                  <Button type="button" size="sm" variant="outline-secondary" disabled={saving} onClick={() => onCopy(option)}
                    aria-label={'Copy ' + option.target.fieldName + ' group ' + option.group.ruleGroupId}>Copy</Button>
                  {onDelete && <Button type="button" size="sm" variant="outline-danger" disabled={saving} onClick={() => onDelete(option)}
                    aria-label={'Delete ' + option.target.fieldName + ' group ' + option.group.ruleGroupId}>Delete</Button>}
                  </div></td>
                </tr>
              );
            }))}
        </tbody>
      </Table>
      <Form.Text>Editing the complete group preserves its other values and dependencies.</Form.Text>
    </section>
  );
};

const EditorModal = ({ editor, data, onClose, onChange, onSave, saving, validation, children }) => {
  if (!editor) return null;
  const { resource, mode, draft } = editor;
  const title = resource === 'valueRules'
    ? `${mode === 'create' ? 'Add' : 'Edit'} rule group`
    : `${mode === 'create' ? 'Add' : 'Edit'} ${RESOURCE_LABELS[resource]}`;
  const update = (field, value) => onChange({ ...draft, [field]: value });
  const currentFieldValues = resource === 'valueRules'
    ? data.fieldValues.filter((value) => value.configFieldId === Number(editor.context?.fieldId))
    : [];
  const dependencyFields = editor.context?.dependencyFields || data.fields;
  const dependencyValues = (editor.context?.dependencyValues || data.fieldValues)
    .filter((value) => value.configFieldId !== Number(editor.context?.fieldId));
  const selectedStation = data.stations.find((station) => station.id === Number(draft.parentStationId));
  const fieldValueFieldType = editor.context?.fieldType || draft.fieldType;
  const isNumericValue = resource === 'fieldValues' && isNumericFieldType(fieldValueFieldType);
  const numericInputStep = isWholeNumberFieldType(fieldValueFieldType) ? '1' : 'any';
  const isHyperlinkValue = resource === 'fieldValues' && isHyperlinkFieldType(fieldValueFieldType);
  const isButtonValue = resource === 'fieldValues' && isButtonFieldType(fieldValueFieldType);
  const updateValueBranch = (branchIndex, changes) => {
    const valueBranches = [...(draft.valueBranches || [])];
    valueBranches[branchIndex] = { ...valueBranches[branchIndex], ...changes };
    update('valueBranches', valueBranches);
  };
  const addValueBranch = () => update('valueBranches', [
    ...(draft.valueBranches || []),
    { clientKey: `value-${++dependencyDraftKey}`, fieldValueId: '', stationRuleId: null, dependencies: [], isDisabled: false, isDefaultValue: false },
  ]);
  const removeValueBranch = (branchIndex) => update(
    'valueBranches',
    (draft.valueBranches || []).filter((branch, index) => index !== branchIndex)
  );
  const updateDependency = (branchIndex, dependencyIndex, parentFieldValueId) => {
    const branch = draft.valueBranches[branchIndex];
    const dependencies = [...(branch.dependencies || [])];
    dependencies[dependencyIndex] = { ...dependencies[dependencyIndex], parentFieldValueId };
    updateValueBranch(branchIndex, { dependencies });
  };
  const addDependency = (branchIndex) => {
    const branch = draft.valueBranches[branchIndex];
    updateValueBranch(branchIndex, { dependencies: [
      ...(branch.dependencies || []),
      { clientKey: `dependency-${++dependencyDraftKey}`, parentFieldValueId: '' },
    ] });
  };
  const removeDependency = (branchIndex, dependencyIndex) => {
    const branch = draft.valueBranches[branchIndex];
    updateValueBranch(branchIndex, {
      dependencies: (branch.dependencies || []).filter((dependency, index) => index !== dependencyIndex),
    });
  };
  const getNamedControlChecked = (elements, name) => {
    const control = elements.namedItem(name);
    if (!control) return false;
    if ('checked' in control) return Boolean(control.checked);
    if (typeof control.length === 'number') {
      return Array.from(control).some((item) => Boolean(item.checked));
    }
    return false;
  };
  const getSubmitDraft = (event) => {
    if (resource !== 'valueRules') return draft;
    const elements = event.currentTarget.elements;
    return {
      ...draft,
      valueBranches: (draft.valueBranches || []).map((branch, branchIndex) => ({
        ...branch,
        isDefaultValue: getNamedControlChecked(elements, `ruleDefault-${branchIndex}`),
        isDisabled: getNamedControlChecked(elements, `ruleDisabled-${branchIndex}`),
      })),
    };
  };

  return (
    <Modal show onHide={saving ? undefined : onClose} centered size={resource === 'valueRules' ? 'lg' : undefined}>
      <Form onSubmit={(event) => { event.preventDefault(); onSave(getSubmitDraft(event)); }}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {children}
          {editor.savedMessage && <Alert variant="success">{editor.savedMessage}</Alert>}
          {validation && <Alert variant="danger">{validation}</Alert>}

          {resource === 'stations' && (
            <Row>
              <Col md={8}><Form.Group className="mb-3"><Form.Label>Station name</Form.Label><Form.Control name="stationName" value={draft.stationName} onChange={(event) => update('stationName', event.target.value)} autoFocus required /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label>Sort order</Form.Label><Form.Control name="sortOrder" type="number" value={draft.sortOrder} onChange={(event) => update('sortOrder', event.target.value)} /></Form.Group></Col>
            </Row>
          )}

          {resource === 'sections' && (
            <>
              <Row>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>Page</Form.Label><Form.Control name="page" value={draft.page} onChange={(event) => update('page', event.target.value)} required /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>Section name</Form.Label><Form.Control name="sectionKey" value={draft.sectionKey} onChange={(event) => update('sectionKey', event.target.value)} required /></Form.Group></Col>
              </Row>
              <Row>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>Display label</Form.Label><Form.Control name="label" value={draft.label} onChange={(event) => update('label', event.target.value)} placeholder="Optional" /></Form.Group></Col>
                <Col md={3}><Form.Group className="mb-3"><Form.Label>Sort order</Form.Label><Form.Control name="sortOrder" type="number" value={draft.sortOrder} onChange={(event) => update('sortOrder', event.target.value)} /></Form.Group></Col>
                <Col md={3}><Form.Group className="mb-3"><Form.Label>Availability</Form.Label><Form.Select name="stationId" value={draft.stationId ?? ''} onChange={(event) => update('stationId', event.target.value)}><option value="">All stations</option>{data.stations.map((station) => <option key={station.id} value={station.id}>{station.stationName}</option>)}</Form.Select></Form.Group></Col>
              </Row>
            </>
          )}

          {resource === 'subSections' && (
            <Row>
              <Col md={8}><Form.Group className="mb-3"><Form.Label>Subsection name</Form.Label><Form.Control name="subSectionName" value={draft.subSectionName} onChange={(event) => update('subSectionName', event.target.value)} required /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label>Sort order</Form.Label><Form.Control name="sortOrder" type="number" value={draft.sortOrder} onChange={(event) => update('sortOrder', event.target.value)} /></Form.Group></Col>
            </Row>
          )}

          {resource === 'fields' && (
            <>
              {mode === 'create' && <Alert variant="info" className="small">The API requires an explicit unique ID when creating a field. The next available ID is prefilled.</Alert>}
              <Row>
                <Col md={3}><Form.Group className="mb-3"><Form.Label>Field ID</Form.Label><Form.Control name="fieldId" type="number" value={draft.id} disabled={mode === 'edit'} onChange={(event) => update('id', event.target.value)} required /></Form.Group></Col>
                <Col md={9}><Form.Group className="mb-3"><Form.Label>Field name</Form.Label><Form.Control name="fieldName" value={draft.fieldName} onChange={(event) => update('fieldName', event.target.value)} required /></Form.Group></Col>
              </Row>
              <Row>
                <Col md={5}><Form.Group className="mb-3"><Form.Label>Field type</Form.Label><Form.Select name="fieldType" value={draft.fieldType} onChange={(event) => update('fieldType', event.target.value)}>{FIELD_TYPES.map((type) => <option key={type}>{type}</option>)}</Form.Select></Form.Group></Col>
                <Col md={4}><Form.Group className="mb-3"><Form.Label>Scope</Form.Label><Form.Select name="scope" value={draft.scope} onChange={(event) => update('scope', event.target.value)}>{FIELD_SCOPES.map((scope) => <option key={scope}>{scope}</option>)}</Form.Select></Form.Group></Col>
                <Col md={3}><Form.Group className="mb-3"><Form.Label>Sort order</Form.Label><Form.Control name="sortOrder" type="number" value={draft.sortOrder} onChange={(event) => update('sortOrder', event.target.value)} /></Form.Group></Col>
              </Row>
            </>
          )}

          {resource === 'fieldValues' && (
            <>
              {isNumericValue ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Allowed number type</Form.Label>
                    <div className="d-flex flex-wrap gap-4">
                      <Form.Check type="radio" name="numericMode" id="numeric-fixed" label="Fixed number" checked={draft.valueMode !== 'range'} onChange={() => onChange({ ...draft, valueMode: 'fixed', minValue: '', maxValue: '' })} />
                      <Form.Check type="radio" name="numericMode" id="numeric-range" label="Minimum and maximum" checked={draft.valueMode === 'range'} onChange={() => onChange({ ...draft, valueMode: 'range', optionValue: '', isDefaultValue: false })} />
                    </div>
                  </Form.Group>
                  {draft.valueMode === 'range' ? (
                    <Row>
                      <Col md={6}><Form.Group className="mb-3"><Form.Label>Minimum value</Form.Label><Form.Control name="minValue" type="number" step={numericInputStep} value={draft.minValue ?? ''} onChange={(event) => update('minValue', event.target.value)} required /></Form.Group></Col>
                      <Col md={6}><Form.Group className="mb-3"><Form.Label>Maximum value</Form.Label><Form.Control name="maxValue" type="number" step={numericInputStep} value={draft.maxValue ?? ''} onChange={(event) => update('maxValue', event.target.value)} required /></Form.Group></Col>
                      <Col xs={12}><Form.Text>The range is inclusive of both the minimum and maximum.</Form.Text></Col>
                    </Row>
                  ) : (
                    <Form.Group className="mb-3"><Form.Label>Fixed number</Form.Label><Form.Control name="optionValue" type="number" step={numericInputStep} value={draft.optionValue} onChange={(event) => update('optionValue', event.target.value)} required /></Form.Group>
                  )}
                </>
              ) : isHyperlinkValue ? (
                <Form.Group className="mb-3">
                  <Form.Label>Link URL</Form.Label>
                  <Form.Control name="optionValue" inputMode="url" value={draft.optionValue} onChange={(event) => update('optionValue', event.target.value)} placeholder="https://example.com/document.pdf" required />
                  <Form.Text>The field name is shown as the display text; this value opens as the link.</Form.Text>
                </Form.Group>
              ) : isButtonValue ? (
                <Form.Group className="mb-3">
                  <Form.Label>Maximum rows</Form.Label>
                  <Form.Control name="maxClicks" type="number" min="1" step="1" value={draft.maxClicks ?? ''} onChange={(event) => update('maxClicks', event.target.value)} required />
                </Form.Group>
              ) : (
                <Form.Group className="mb-3"><Form.Label>Option value</Form.Label><Form.Control name="optionValue" value={draft.optionValue} onChange={(event) => update('optionValue', event.target.value)} required /></Form.Group>
              )}
              {(!isNumericValue || draft.valueMode !== 'range') && <Form.Check name="isDefaultValue" type="switch" label="General default value" checked={draft.isDefaultValue} onChange={(event) => update('isDefaultValue', event.target.checked)} />}
            </>
          )}

          {resource === 'valueRules' && (
            <>
              <Alert variant="info" className="small">
                This modal saves one complete rule group. Values are grouped inside it, and every condition becomes its own API row with exactly one parent type.
              </Alert>
              <Form.Group className="mb-3 config-admin-group-id">
                <Form.Label>Rule group ID</Form.Label>
                <Form.Control name="ruleGroupId" type="number" min="1" value={draft.ruleGroupId} onChange={(event) => update('ruleGroupId', event.target.value)} required />
              </Form.Group>
              <div className="config-admin-value-branches">
                {(draft.valueBranches || []).map((branch, branchIndex) => (
                  <React.Fragment key={branch.clientKey || branch.stationRuleId || branchIndex}>
                    {branchIndex > 0 && <div className="config-admin-value-or">OR allowed value</div>}
                    <div className="config-admin-value-branch">
                      <div className="config-admin-value-branch-header">
                        <Form.Group className="flex-grow-1 mb-0">
                          <Form.Label>Allowed field value {branchIndex + 1}</Form.Label>
                          <Form.Select value={branch.fieldValueId} onChange={(event) => updateValueBranch(branchIndex, { fieldValueId: event.target.value })} required>
                            <option value="">Choose a value</option>
                            {currentFieldValues.map((value) => <option key={value.id} value={value.id}>{getFieldValueLabel(value)} (ID {value.id})</option>)}
                          </Form.Select>
                        </Form.Group>
                        {(draft.valueBranches || []).length > 1 && <Button type="button" variant="outline-danger" onClick={() => removeValueBranch(branchIndex)} aria-label={`Remove allowed value ${branchIndex + 1}`}><i className="bi bi-trash" aria-hidden="true"></i> Remove value</Button>}
                      </div>
                      <div className="config-admin-condition-list">
                        <div className="config-admin-condition-row is-station">
                          <span className="config-admin-condition-type">First condition</span>
                          <div><strong>Station is {selectedStation?.stationName || `Station ${draft.parentStationId}`}</strong><small>Posts with ParentStationId {draft.parentStationId} and no parent field value.</small></div>
                        </div>
                        {(branch.dependencies || []).map((dependency, dependencyIndex) => (
                          <React.Fragment key={dependency.id || dependency.clientKey || dependencyIndex}>
                            <div className="config-admin-condition-and">AND</div>
                            <div className="config-admin-condition-row">
                              <Form.Group className="flex-grow-1 mb-0">
                                <Form.Label>Required field value</Form.Label>
                                <Form.Select value={dependency.parentFieldValueId ?? ''} onChange={(event) => updateDependency(branchIndex, dependencyIndex, event.target.value)} required>
                                  <option value="">Choose a dependency</option>
                                  {dependencyValues.map((value) => { const field = dependencyFields.find((item) => item.id === value.configFieldId); return <option key={value.id} value={value.id}>{field?.fieldName || `Field ${value.configFieldId}`} — {getFieldValueLabel(value)}</option>; })}
                                </Form.Select>
                              </Form.Group>
                              <Button type="button" variant="outline-danger" className="config-admin-remove-condition" onClick={() => removeDependency(branchIndex, dependencyIndex)} aria-label={`Remove AND condition ${dependencyIndex + 1} from allowed value ${branchIndex + 1}`}><i className="bi bi-trash" aria-hidden="true"></i></Button>
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                      <Button type="button" variant="outline-primary" size="sm" onClick={() => addDependency(branchIndex)}><i className="bi bi-plus-circle me-2" aria-hidden="true"></i>Add AND condition</Button>
                      <div className="d-flex flex-wrap gap-4 mt-3">
                        <Form.Check id={`ruleDefault-${branch.clientKey || branchIndex}`} name={`ruleDefault-${branchIndex}`} type="switch" label="Default for this rule context" checked={Boolean(branch.isDefaultValue)} onChange={(event) => updateValueBranch(branchIndex, { isDefaultValue: Boolean(event.target.checked) })} />
                        <Form.Check id={`ruleDisabled-${branch.clientKey || branchIndex}`} name={`ruleDisabled-${branchIndex}`} type="switch" label="Show as disabled" checked={Boolean(branch.isDisabled)} onChange={(event) => updateValueBranch(branchIndex, { isDisabled: Boolean(event.target.checked) })} />
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <Button type="button" variant="outline-primary" className="mt-3" onClick={addValueBranch}><i className="bi bi-plus-circle me-2" aria-hidden="true"></i>Add allowed value</Button>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>{editor.savedMessage ? 'Done' : 'Cancel'}</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            {resource === 'valueRules' ? (mode === 'create' ? 'Create rule group' : 'Save rule group') : (mode === 'create' ? 'Create' : 'Save changes')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export const ConfigurationAdminContent = ({ canEditSales }) => {
  const [globalRulesOpen, setGlobalRulesOpen] = useState(false);
  const [pricingFormulasOpen, setPricingFormulasOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const endpoints = useSalesEndpoints();
  const request = usePortalApiRequest();
  const stationId = Number(searchParams.get('station') || 0);
  const sectionId = Number(searchParams.get('section') || 0);
  const subSectionId = Number(searchParams.get('subsection') || 0);
  const fieldId = Number(searchParams.get('field') || 0);
  const isPartsView = Boolean(stationId) && searchParams.get('view') === 'parts';
  const isPartAutoSelectView = Boolean(stationId) && searchParams.get('view') === 'part-auto-select';
  const isStationChildView = isPartsView || isPartAutoSelectView;
  const { data, loading, error, setError, load, mutate, mutateBatch, fetchResource, loadedScopes } = useConfigurationAdminData({
    stationId,
    sectionId,
    subSectionId,
    fieldId,
    configurationEnabled: !isStationChildView,
  });
  const {
    coverage,
    coverageLoading,
    coverageError,
    refreshCoverage,
  } = useConfigurationCoverage({
    stationId,
    enabled: !isStationChildView && loadedScopes.sections === 'all' && !loading,
    fetchResource,
  });
  const [editor, setEditor] = useState(null);
  const [validation, setValidation] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [ruleReferences, setRuleReferences] = useState({ fieldId: 0, fields: [], fieldValues: [] });
  const [previewCache, setPreviewCache] = useState({});
  const [previewSelectionsByStation, setPreviewSelectionsByStation] = useState({});
  const [previewInputsByStation, setPreviewInputsByStation] = useState({});
  const [previewPumpRowsByStation, updatePreviewPumpRows, setPreviewPumpRowsByStation] = usePreviewPumpRows();
  const [previewModal, setPreviewModal] = useState(null);
  const [pumpTableModal, setPumpTableModal] = useState(null);
  const [pumpAssignmentView, setPumpAssignmentView] = useState(false);
  const ruleSectionRef = useRef(null);
  const pumpRuleChangesPending = useRef(false);
  const focusedBranchKeys = searchParams.get('branches') || '';
  const [autoPickPreviewCache, setAutoPickPreviewCache] = useState({});
  const autoPickPreviewRequestVersion = useRef(0);
  const [ruleSearch, setRuleSearch] = useState('');
  const [ruleFilter, setRuleFilter] = useState('all');
  const [ruleDependencyFilters, setRuleDependencyFilters] = useState({});
  const [partsRefreshVersion, setPartsRefreshVersion] = useState(0);
  const [partAutoSelectRefreshVersion, setPartAutoSelectRefreshVersion] = useState(0);
  const [bulkValueEditorOpen, setBulkValueEditorOpen] = useState(false);
  const [bulkValueValidation, setBulkValueValidation] = useState('');
  const [bulkRuleEditorOpen, setBulkRuleEditorOpen] = useState(false);
  const [bulkRuleValidation, setBulkRuleValidation] = useState('');

  useEffect(() => {
    setRuleSearch('');
    setRuleFilter('all');
    setRuleDependencyFilters({});
  }, [fieldId, stationId]);

  useEffect(() => {
    if (focusedBranchKeys && !loading) ruleSectionRef.current?.scrollIntoView?.({ block: 'start' });
  }, [focusedBranchKeys, fieldId, loading]);

  const clearPreviewCache = useCallback(() => {
    autoPickPreviewRequestVersion.current += 1;
    setPreviewCache({});
    setAutoPickPreviewCache({});
  }, []);

  const loadPreviewData = useCallback(async (targetStationId, force = false) => {
    const numericStationId = Number(targetStationId);
    if (!numericStationId) return null;

    const cached = previewCache[numericStationId];
    if (!force && cached?.status === 'ready') return cached.data;
    if (!force && cached?.status === 'loading') return null;

    setPreviewCache((current) => ({
      ...current,
      [numericStationId]: { ...(current[numericStationId] || {}), status: 'loading', error: '' },
    }));

    try {
      const rows = {};
      for (const resource of PREVIEW_RESOURCES) {
        rows[resource] = await fetchResource(resource);
      }
      rows.advancedRules = getSalesArrayPayload(await request({ method: 'GET', url: endpoints.advancedConfigRules.base })).map(normalizeAdvancedRule);
      try {
        rows.pricingFormulas = getSalesArrayPayload(await request({ method: 'GET', url: endpoints.pricingFormulas.base }));
      } catch {
        rows.pricingFormulas = [];
        rows.pricingError = 'Pricing formulas could not load. Refresh the preview to retry.';
      }
      setPreviewCache((current) => ({
        ...current,
        [numericStationId]: { status: 'ready', error: '', data: rows },
      }));
      return rows;
    } catch (requestError) {
      const errorMessage = formatSalesApiError(requestError, 'Unable to load the configuration preview.');
      setPreviewCache((current) => ({
        ...current,
        [numericStationId]: { ...(current[numericStationId] || {}), status: 'error', error: errorMessage },
      }));
      return null;
    }
  }, [fetchResource, previewCache, request, endpoints]);

  const loadAutoPickPreviewData = useCallback(async (targetStationId, force = false) => {
    const numericStationId = Number(targetStationId);
    if (!numericStationId) return null;

    const cached = autoPickPreviewCache[numericStationId];
    if (!force && cached?.status === 'ready') return cached.data;
    if (!force && cached?.status === 'loading') return null;
    const requestVersion = autoPickPreviewRequestVersion.current + 1;
    autoPickPreviewRequestVersion.current = requestVersion;

    setAutoPickPreviewCache((current) => ({
      ...current,
      [numericStationId]: { ...(current[numericStationId] || {}), status: 'loading', error: '' },
    }));

    try {
      // Load only when Auto Pick is requested and keep the calls sequential so
      // the Sales API is not hit with a burst of unrelated reads.
      const assignmentResponse = await request({
        method: 'GET',
        url: endpoints.partStations.base,
        params: { stationid: numericStationId },
      });
      if (requestVersion !== autoPickPreviewRequestVersion.current) return null;
      const assignments = getSalesArrayPayload(assignmentResponse)
        .map(normalizeStationPartAssignment)
        .filter((assignment) => assignment.stationId === numericStationId);
      const partResponse = await request({ method: 'GET', url: endpoints.parts.base });
      if (requestVersion !== autoPickPreviewRequestVersion.current) return null;
      const parts = getSalesArrayPayload(partResponse).map(normalizeStationPart);
      const categoryResponse = await request({ method: 'GET', url: endpoints.categories.base });
      if (requestVersion !== autoPickPreviewRequestVersion.current) return null;
      const categories = getSalesArrayPayload(categoryResponse).map(normalizeStationPartCategory);
      const ruleResponse = await request({ method: 'GET', url: endpoints.partMatchRules.base });
      if (requestVersion !== autoPickPreviewRequestVersion.current) return null;
      const rules = getSalesArrayPayload(ruleResponse).map(normalizePartMatchRule);
      const nextData = { assignments, parts, categories, rules };

      setAutoPickPreviewCache((current) => ({
        ...current,
        [numericStationId]: { status: 'ready', error: '', data: nextData },
      }));
      return nextData;
    } catch (requestError) {
      if (requestVersion !== autoPickPreviewRequestVersion.current) return null;
      const errorMessage = formatSalesApiError(requestError, 'Unable to load Part Auto Select preview data.');
      setAutoPickPreviewCache((current) => ({
        ...current,
        [numericStationId]: {
          ...(current[numericStationId] || {}),
          status: 'error',
          error: errorMessage,
        },
      }));
      return null;
    }
  }, [autoPickPreviewCache, endpoints, request]);

  const updatePreviewSelection = useCallback((targetStationId, fieldId, valueId) => {
    setPreviewSelectionsByStation((current) => current[targetStationId]?.[fieldId] === Number(valueId) ? current : ({
      ...current,
      [targetStationId]: {
        ...(current[targetStationId] || {}),
        [fieldId]: Number(valueId),
      },
    }));
  }, []);

  const updatePreviewInput = useCallback((targetStationId, fieldId, value) => {
    setPreviewInputsByStation((current) => current[targetStationId]?.[fieldId] === value ? current : ({
      ...current,
      [targetStationId]: {
        ...(current[targetStationId] || {}),
        [fieldId]: value,
      },
    }));
  }, []);

  useEffect(() => {
    if (!fieldId || loadedScopes.valueRules !== 'all' || ruleReferences.fieldId === fieldId) return undefined;
    let cancelled = false;
    const loadReferences = async () => {
      try {
        const fields = await fetchResource('fields');
        const fieldValues = await fetchResource('fieldValues');
        if (!cancelled) setRuleReferences({ fieldId, fields, fieldValues });
      } catch {
        // Rule groups remain usable with ID fallbacks when reference labels cannot be loaded.
      }
    };
    loadReferences();
    return () => {
      cancelled = true;
    };
  }, [fetchResource, fieldId, loadedScopes.valueRules, ruleReferences.fieldId]);

  const selectedStation = data.stations.find((station) => station.id === stationId);
  const selectedSection = data.sections.find((section) => section.id === sectionId);
  const selectedSubSection = data.subSections.find((subSection) => subSection.id === subSectionId);
  const selectedField = data.fields.find((field) => field.id === fieldId);
  const stationPreviewOptions = useMemo(
    () => sortByOrderThenName(data.stations, 'stationName'),
    [data.stations]
  );
  const selectedPreviewState = stationId ? previewCache[stationId] : null;
  const selectedPreviewSelections = useMemo(
    () => previewSelectionsByStation[stationId] || {},
    [previewSelectionsByStation, stationId]
  );
  const selectedPreviewInputs = useMemo(
    () => previewInputsByStation[stationId] || {},
    [previewInputsByStation, stationId]
  );
  const sectionCoverageById = useMemo(
    () => new Map((coverage?.sections || []).map((item) => [item.sectionId, item])),
    [coverage]
  );
  const subSectionCoverageById = useMemo(
    () => new Map((coverage?.subSections || []).map((item) => [item.subSectionId, item])),
    [coverage]
  );
  const fieldCoverageById = useMemo(
    () => new Map((coverage?.fields || []).map((item) => [item.fieldId, item])),
    [coverage]
  );
  const selectedStationPreviewModel = useMemo(
    () => selectedPreviewState?.status === 'ready'
      ? buildConfigurationPreviewModel(selectedPreviewState.data, stationId, null, selectedPreviewSelections, selectedPreviewInputs)
      : null,
    [selectedPreviewInputs, selectedPreviewSelections, selectedPreviewState, stationId]
  );
  const selectedSectionPreviewModel = useMemo(
    () => selectedPreviewState?.status === 'ready' && sectionId
      ? buildConfigurationPreviewModel(selectedPreviewState.data, stationId, sectionId, selectedPreviewSelections, selectedPreviewInputs)
      : null,
    [sectionId, selectedPreviewInputs, selectedPreviewSelections, selectedPreviewState, stationId]
  );
  const selectedPumpTableModel = useMemo(
    () => selectedPreviewState?.status === 'ready'
      ? buildPumpTablePreviewModel(selectedPreviewState.data, stationId)
      : null,
    [selectedPreviewState, stationId]
  );

  useEffect(() => {
    if (!stationId || isStationChildView) return;
    loadPreviewData(stationId);
  }, [isStationChildView, loadPreviewData, stationId]);

  const previewModalStation = previewModal?.stationId
    ? data.stations.find((station) => station.id === previewModal.stationId)
    : null;
  const previewModalState = previewModal?.stationId ? previewCache[previewModal.stationId] : null;
  const previewModalSelections = useMemo(
    () => previewSelectionsByStation[previewModal?.stationId] || {},
    [previewSelectionsByStation, previewModal]
  );
  const previewModalInputs = useMemo(
    () => previewInputsByStation[previewModal?.stationId] || {},
    [previewInputsByStation, previewModal]
  );
  const previewModalModel = useMemo(
    () => previewModalState?.status === 'ready'
      ? buildConfigurationPreviewModel(previewModalState.data, previewModal.stationId, null, previewModalSelections, previewModalInputs)
      : null,
    [previewModal, previewModalInputs, previewModalSelections, previewModalState]
  );
  const previewAutoPickState = previewModal?.stationId
    ? autoPickPreviewCache[previewModal.stationId]
    : null;
  const previewModalPumpRows = useMemo(
    () => previewPumpRowsByStation[previewModal?.stationId] || [],
    [previewModal, previewPumpRowsByStation]
  );
  const previewAutoPickModel = useMemo(
    () => previewAutoPickState?.status === 'ready' && previewModalModel
      ? buildPartAutoPickPreviewModel(
          {
            ...previewAutoPickState.data,
            fields: previewModalState?.data?.fields || [],
          },
          previewModal.stationId,
          previewModalModel.effectiveSelections,
          previewModalModel.effectiveInputs,
          previewModalPumpRows
        )
      : null,
    [previewAutoPickState, previewModal, previewModalModel, previewModalPumpRows, previewModalState]
  );
  const previewModalPumpTableModel = useMemo(
    () => previewModalState?.status === 'ready'
      ? buildPumpTablePreviewModel(previewModalState.data, previewModal.stationId)
      : null,
    [previewModal, previewModalState]
  );
  const pumpTableStation = pumpTableModal?.stationId
    ? data.stations.find((station) => station.id === pumpTableModal.stationId)
    : null;
  const pumpTableState = pumpTableModal?.stationId ? previewCache[pumpTableModal.stationId] : null;
  const pumpTableModel = useMemo(
    () => pumpTableState?.status === 'ready'
      ? buildPumpTablePreviewModel(pumpTableState.data, pumpTableModal.stationId)
      : null,
    [pumpTableModal, pumpTableState]
  );

  const setPath = useCallback((updates, remove = []) => {
    const next = new URLSearchParams(searchParams);
    if (!Object.prototype.hasOwnProperty.call(updates, 'branches')) {
      next.delete('branches');
      next.delete('pumpCell');
    }
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    remove.forEach((key) => next.delete(key));
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const openPumpCellRules = (cell, label, targetStationId) => {
    if (!canEditSales) return;
    const source = previewCache[targetStationId]?.data;
    if (!source || !cell.ruleTargets?.length) return;
    const options = cell.ruleTargets.flatMap((target) => {
      const ids = new Set(source.fieldValues.filter((value) => value.configFieldId === target.fieldId).map((value) => value.id));
      const groups = getValueRuleGroups(source.valueRules, targetStationId, ids);
      return groups.filter((group) => group.values.some((branch) => target.branchKeys.includes(branch.key)))
        .map((group) => ({ target, group, groups }));
    });
    if (!options.length) return;
    openPumpRuleOption(options[0], { options, source, stationId: targetStationId, label });
  };

  const openPumpRuleOption = (option, pump) => {
    const field = pump.source.fields.find((item) => item.id === option.target.fieldId);
    return openEdit('valueRules', option.group, {
      fieldId: option.target.fieldId, fieldType: field?.fieldType, stationId: pump.stationId,
      groupKey: option.group.key, ruleGroups: option.groups, pump,
      editorData: { ...data, ...pump.source },
      dependencyFields: pump.source.fields, dependencyValues: pump.source.fieldValues,
    });
  };

  const copyPumpRuleOption = async (option, pump) => {
    if (!canEditSales || saving) return;
    if (editor?.dirty && !window.confirm('Discard unsaved rule edits and copy this group?')) return;
    setSaving(true);
    setValidation('');
    try {
      const rules = await fetchResource('valueRules');
      const field = pump.source.fields.find((item) => item.id === option.target.fieldId);
      const ids = new Set(pump.source.fieldValues.filter((value) => value.configFieldId === field.id).map((value) => value.id));
      const groups = getValueRuleGroups(rules, pump.stationId, ids);
      const sourceGroup = groups.find((group) => group.key === option.group.key);
      if (!sourceGroup) throw new Error('The source rule group no longer exists. Reopen the pump table to refresh its rules.');
      setEditor({
        resource: 'valueRules', mode: 'create', dirty: true,
        draft: copyValueRuleGroupDraft(sourceGroup, pump.stationId, rules),
        context: {
          fieldId: field.id, fieldType: field.fieldType, stationId: pump.stationId,
          ruleGroups: groups, existingRows: [], pump, copiedGroupId: sourceGroup.ruleGroupId,
          editorData: { ...data, ...pump.source },
          dependencyFields: pump.source.fields, dependencyValues: pump.source.fieldValues,
        },
      });
    } catch (requestError) {
      setValidation(formatSalesApiError(requestError, 'Unable to copy the rule group.'));
    } finally {
      setSaving(false);
    }
  };

  const deletePumpRuleOption = async (option, pump) => {
    if (!canEditSales || saving) return;
    const group = option.group;
    const warning = editor?.dirty ? '\nUnsaved edits in this modal will be discarded.' : '';
    if (!window.confirm(`Delete ${option.target.fieldName} rule group ${group.ruleGroupId} for this station (${group.rows.length} condition rows)? This removes the complete group, including values not shown in this cell, and affects every cell that uses it. Dependency rows still used by another station will be retained. This cannot be undone.${warning}`)) return;
    setSaving(true);
    setValidation('');
    let deletionStarted = false;
    try {
      const rules = await fetchResource('valueRules');
      const ids = new Set(pump.source.fieldValues.filter(value => value.configFieldId === option.target.fieldId).map(value => value.id));
      const current = getValueRuleGroups(rules, pump.stationId, ids).find(item => item.key === group.key);
      const snapshot = rows => JSON.stringify([...rows].sort((a, b) => a.id - b.id));
      if (!current || snapshot(current.rows) !== snapshot(group.rows)) throw new Error('This group changed on the server. Close and reopen the pump table before deleting it.');
      deletionStarted = true;
      const removableRows = current.rows.filter(rule => rule.parentStationId !== null || !rules.some(other =>
        other.ruleGroupId === rule.ruleGroupId && other.fieldValueId === rule.fieldValueId
        && other.parentStationId !== null && Number(other.parentStationId) !== Number(pump.stationId)));
      const nextRules = await mutateBatch('valueRules', removableRows.map(rule => ({ method: 'DELETE', payload: { Id: rule.id } })));
      setPreviewCache(previous => ({ ...previous, [pump.stationId]: { status: 'ready', error: '', data: { ...pump.source, valueRules: nextRules } } }));
      pumpRuleChangesPending.current = true;
      setEditor(null);
      setNotice(`Rule group ${group.ruleGroupId} deleted successfully.`);
    } catch (requestError) {
      if (deletionStarted) {
        // A batch may have partially committed. Discard stale editor state and
        // invalidate the matrix rather than allowing edits against deleted IDs.
        setEditor(null);
        setPreviewCache(previous => ({ ...previous, [pump.stationId]: { status: 'error', error: `${formatSalesApiError(requestError, 'Unable to delete the complete rule group.')} ${requestError.completedCount || 0} rows confirmed deleted. Close and reopen the pump table to reload before continuing.` } }));
        pumpRuleChangesPending.current = true;
      } else setValidation(formatSalesApiError(requestError, 'Unable to delete the rule group.'));
    } finally {
      setSaving(false);
    }
  };

  const selectStation = (id) => setPath({ station: id }, ['section', 'subsection', 'field', 'view']);
  const selectSection = (id) => setPath({ section: id }, ['subsection', 'field', 'view']);
  const selectSubSection = (id) => setPath({ subsection: id }, ['field', 'view']);
  const selectField = (id) => setPath({ field: id }, ['view']);
  const selectParts = () => {
    if (selectedStation) setPath({ view: 'parts' }, ['section', 'subsection', 'field']);
  };
  const selectStationChild = (view) => {
    if (view === 'parts') selectParts();
    if (view === 'part-auto-select' && selectedStation) {
      setPath({ view: 'part-auto-select' }, ['section', 'subsection', 'field']);
    }
  };

  const openStationPreview = async (station) => {
    setPreviewModal({ stationId: station.id });
    await loadPreviewData(station.id);
  };

  const openPumpTablePreview = async (station) => {
    setPumpAssignmentView(false);
    setPumpTableModal({ stationId: station.id });
    await loadPreviewData(station.id, true);
  };

  const openStationTypePreview = async () => {
    const initialStation = stationPreviewOptions[0];
    if (!initialStation) return;
    setPreviewModal({ stationId: initialStation.id, allowStationSelect: true });
    await loadPreviewData(initialStation.id);
  };

  const changePreviewModalStation = async (nextStationId) => {
    if (!nextStationId) return;
    setPreviewModal((current) => ({ ...(current || {}), stationId: nextStationId }));
    await loadPreviewData(nextStationId);
  };

  const runPreviewAutoPick = async () => {
    if (!previewModal?.stationId) return;
    await loadAutoPickPreviewData(previewModal.stationId);
  };

  const closeStationPreview = useCallback(() => {
    autoPickPreviewRequestVersion.current += 1;
    setAutoPickPreviewCache({});
    setPreviewPumpRowsByStation({});
    setPreviewModal(null);
    if (pumpRuleChangesPending.current) {
      pumpRuleChangesPending.current = false;
      refreshCoverage();
    }
  }, [refreshCoverage, setPreviewPumpRowsByStation]);

  const closePumpTablePreview = useCallback(() => {
    setPumpTableModal(null);
    if (pumpRuleChangesPending.current) {
      pumpRuleChangesPending.current = false;
      refreshCoverage();
    }
  }, [refreshCoverage]);

  const stationSections = useMemo(
    () => (stationId ? getSectionsForStation(data.sections, stationId) : []),
    [data.sections, stationId]
  );
  const sectionSubSections = useMemo(
    () => sortByOrderThenName(data.subSections.filter((item) => item.sectionId === sectionId), 'subSectionName'),
    [data.subSections, sectionId]
  );
  const subSectionFields = useMemo(
    () => sortByOrderThenName(data.fields.filter((item) => item.subSectionId === subSectionId), 'fieldName'),
    [data.fields, subSectionId]
  );
  const fieldValues = useMemo(
    () => data.fieldValues
      .filter((item) => item.configFieldId === fieldId)
      .sort((left, right) => getFieldValueLabel(left).localeCompare(getFieldValueLabel(right), undefined, { numeric: true })),
    [data.fieldValues, fieldId]
  );
  const fieldValueIds = useMemo(() => new Set(fieldValues.map((value) => value.id)), [fieldValues]);
  const fieldRuleGroups = useMemo(
    () => getValueRuleGroups(data.valueRules, stationId, fieldValueIds),
    [data.valueRules, fieldValueIds, stationId]
  );
  const fieldRuleRows = useMemo(
    () => [...new Map(fieldRuleGroups.flatMap((group) => group.rows).map((rule) => [rule.id, rule])).values()],
    [fieldRuleGroups]
  );
  const ruleReferenceFields = ruleReferences.fieldId === fieldId ? ruleReferences.fields : data.fields;
  const ruleReferenceValues = ruleReferences.fieldId === fieldId ? ruleReferences.fieldValues : data.fieldValues;
  const ruleDependencyFilterOptions = useMemo(
    () => getValueRuleDependencyFilterOptions(
      fieldRuleGroups,
      ruleReferenceValues,
      ruleReferenceFields
    ),
    [fieldRuleGroups, ruleReferenceFields, ruleReferenceValues]
  );
  const filteredFieldRuleGroups = useMemo(
    () => filterValueRuleGroups(
      focusedBranchKeys ? fieldRuleGroups.flatMap((group) => {
        const keys = new Set(focusedBranchKeys.split(','));
        const values = group.values.filter((branch) => keys.has(branch.key));
        return values.length ? [{ ...group, values, rows: values.flatMap((branch) => branch.rows) }] : [];
      }) : fieldRuleGroups,
      ruleSearch,
      ruleFilter,
      ruleReferenceValues,
      ruleReferenceFields,
      ruleDependencyFilters
    ),
    [fieldRuleGroups, focusedBranchKeys, ruleDependencyFilters, ruleFilter, ruleReferenceFields, ruleReferenceValues, ruleSearch]
  );
  const filteredRuleValueCount = useMemo(
    () => filteredFieldRuleGroups.reduce((total, group) => total + group.values.length, 0),
    [filteredFieldRuleGroups]
  );

  const loadRuleEditorContext = async () => {
    if (ruleReferences.fieldId === fieldId && ruleReferences.fields.length > 0) {
      return { dependencyFields: ruleReferences.fields, dependencyValues: ruleReferences.fieldValues };
    }
    const dependencyFields = await fetchResource('fields');
    const dependencyValues = await fetchResource('fieldValues');
    setRuleReferences({ fieldId, fields: dependencyFields, fieldValues: dependencyValues });
    return { dependencyFields, dependencyValues };
  };

  const openCreate = async (resource, context = {}) => {
    let nextFieldId = getNextId(data.fields);
    let additionalContext = {};
    try {
      if (resource === 'fields') {
        const allFields = await fetchResource('fields');
        nextFieldId = getNextId(allFields);
        additionalContext = { allFields };
      }
      if (resource === 'valueRules') additionalContext = await loadRuleEditorContext();
    } catch (requestError) {
      setError(formatSalesApiError(requestError, `Unable to prepare the ${RESOURCE_LABELS[resource]} editor.`));
      return;
    }

    const drafts = {
      stations: { stationName: '', sortOrder: getNextId(data.stations) },
      sections: { stationId: stationId || '', page: 'Quote', sectionKey: '', label: '', sortOrder: stationSections.length + 1 },
      subSections: { sectionId, subSectionName: '', sortOrder: sectionSubSections.length + 1 },
      fields: { id: nextFieldId, subSectionId, fieldName: '', fieldType: 'Dropdown', scope: 'Zone', sortOrder: subSectionFields.length + 1 },
      fieldValues: { configFieldId: fieldId, fieldType: selectedField?.fieldType, valueMode: 'fixed', optionValue: '', minValue: '', maxValue: '', maxClicks: '', isDefaultValue: false },
      valueRules: buildEmptyValueRuleGroupDraft(stationId, data.valueRules, fieldValues[0]?.id || ''),
    };
    setValidation('');
    setEditor({ resource, mode: 'create', draft: drafts[resource], context: { fieldId, fieldType: selectedField?.fieldType, ...context, ...additionalContext } });
  };

  const openEdit = async (resource, record, context = {}) => {
    let additionalContext = {};
    try {
      if (resource === 'valueRules') additionalContext = context.pump
        ? { dependencyFields: context.dependencyFields, dependencyValues: context.dependencyValues }
        : await loadRuleEditorContext();
    } catch (requestError) {
      setError(formatSalesApiError(requestError, 'Unable to prepare the value rule editor.'));
      return;
    }
    let draft;
    if (resource === 'fieldValues') {
      draft = { ...record, fieldType: selectedField?.fieldType, valueMode: getFieldValueMode(record) };
    } else if (resource === 'valueRules') {
      draft = {
        ruleGroupId: record.ruleGroupId,
        parentStationId: context.stationId ?? stationId,
        valueBranches: record.values.map((value) => ({
          clientKey: `value-${++dependencyDraftKey}`,
          fieldValueId: value.fieldValueId,
          stationRuleId: value.stationRule?.id,
          dependencies: [
            ...value.dependencies.map((dependency) => ({
              id: dependency.id,
              clientKey: `dependency-${++dependencyDraftKey}`,
              parentFieldValueId: dependency.parentFieldValueId,
            })),
            ...(value.stationRule?.parentFieldValueId ? [{ clientKey: `dependency-${++dependencyDraftKey}`, parentFieldValueId: value.stationRule.parentFieldValueId }] : []),
          ],
          isDisabled: value.isDisabled,
          isDefaultValue: value.isDefaultValue,
        })),
      };
      additionalContext.existingRows = record.rows;
    } else {
      draft = { ...record };
    }
    setValidation('');
    setEditor({ resource, mode: 'edit', draft, context: { fieldId, fieldType: selectedField?.fieldType, ...context, ...additionalContext } });
  };

  const openBulkValueRules = async () => {
    try {
      await loadRuleEditorContext();
      setBulkRuleValidation('');
      setBulkRuleEditorOpen(true);
    } catch (requestError) {
      setError(formatSalesApiError(requestError, 'Unable to prepare the bulk value rule editor.'));
    }
  };

  const saveEditor = async (submittedDraft) => {
    if (!editor || !canEditSales) return;
    const draft = submittedDraft || editor.draft;
    const basicValidation = editor.resource === 'valueRules'
      ? validateValueRuleGroupDraft(draft)
      : validateDraft(editor.resource, draft);
    if (basicValidation) {
      setValidation(basicValidation);
      return;
    }

    const validationFields = editor.context?.allFields || data.fields;
    if (editor.resource === 'fields' && editor.mode === 'create' && validationFields.some((field) => field.id === Number(draft.id))) {
      setValidation('That field ID is already in use. Choose another ID.');
      return;
    }

    if (editor.resource === 'fieldValues' && draft.isDefaultValue) {
      const duplicateDefault = data.fieldValues.some((value) => value.configFieldId === Number(draft.configFieldId) && value.isDefaultValue && value.id !== Number(draft.id || 0));
      if (duplicateDefault) {
        setValidation('This field already has a general default. Edit that value first.');
        return;
      }
    }

    if (editor.resource === 'valueRules') {
      const conflictingGroup = (editor.context?.ruleGroups || fieldRuleGroups).some((group) => (
        group.key !== editor.context?.groupKey &&
        group.ruleGroupId === Number(draft.ruleGroupId)
      ));
      if (conflictingGroup) {
        setValidation('That Rule Group ID already exists for this station and field. Edit the existing group instead.');
        return;
      }
    }

    if (editor.resource === 'valueRules' && draft.valueBranches.some((branch) => branch.isDefaultValue)) {
      const duplicateDefault = (editor.context?.ruleGroups || fieldRuleGroups).some((group) => (
        group.key !== editor.context?.groupKey &&
        group.values.some((value) => value.isDefaultValue)
      ));
      if (duplicateDefault) {
        setValidation('This field already has a different default value for this station. Edit that rule group first.');
        return;
      }
    }

    setSaving(true);
    setValidation('');
    setError('');
    try {
      let savedValueRules = null;
      if (editor.resource === 'valueRules') {
        const operations = buildValueRuleGroupOperations(draft, editor.context?.existingRows || []);
        savedValueRules = await mutateBatch('valueRules', operations);
        setNotice(`Rule group ${draft.ruleGroupId} ${editor.mode === 'create' ? 'created' : 'updated'} successfully.`);
      } else {
        const payload = buildPayload(editor.resource, draft, editor.mode);
        await mutate(editor.resource, editor.mode === 'create' ? 'POST' : 'PUT', payload);
        setNotice(`${RESOURCE_LABELS[editor.resource][0].toUpperCase()}${RESOURCE_LABELS[editor.resource].slice(1)} ${editor.mode === 'create' ? 'created' : 'updated'} successfully.`);
      }

      if (editor.resource === 'valueRules' && editor.context?.pump) {
        const pump = editor.context.pump;
        const targetStationId = Number(pump.stationId);
        const nextSource = { ...pump.source, valueRules: savedValueRules };
        setPreviewCache((current) => {
          const cached = current[targetStationId];
          return {
            ...current,
            [targetStationId]: {
              status: 'ready',
              error: '',
              data: {
                ...nextSource,
                ...(cached?.data || {}),
                valueRules: savedValueRules,
              },
            },
          };
        });
        pumpRuleChangesPending.current = true;

        if (editor.mode === 'create') {
          const targetFieldValueIds = new Set(nextSource.fieldValues
            .filter((value) => value.configFieldId === Number(editor.context.fieldId))
            .map((value) => value.id));
          const nextRuleGroups = getValueRuleGroups(savedValueRules, targetStationId, targetFieldValueIds);
          setEditor({
            resource: 'valueRules',
            mode: 'create',
            dirty: false,
            savedMessage: `Rule group ${draft.ruleGroupId} created. The editor is ready for another rule group.`,
            draft: buildEmptyValueRuleGroupDraft(targetStationId, savedValueRules),
            context: {
              ...editor.context,
              groupKey: null,
              copiedGroupId: null,
              existingRows: [],
              ruleGroups: nextRuleGroups,
              pump: { ...pump, source: nextSource },
              editorData: { ...editor.context.editorData, valueRules: savedValueRules },
            },
          });
        } else {
          setEditor(null);
        }
        return;
      }

      clearPreviewCache();
      setEditor(null);
      refreshCoverage();
    } catch (requestError) {
      const progress = requestError.completedCount > 0
        ? ` ${requestError.completedCount} row(s) were saved before the error; the latest server state has been reloaded.`
        : '';
      setValidation(`${formatSalesApiError(requestError, `Unable to save ${RESOURCE_LABELS[editor.resource]}.`)}${progress}`);
      if (editor.resource === 'valueRules') {
        try {
          await load();
        } catch {
          // Keep the original save error visible if recovery refresh fails.
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const saveBulkFieldValues = async ({ operations, baselineValues }) => {
    if (!selectedField || operations.length === 0) return;
    setSaving(true);
    setBulkValueValidation('');
    setError('');
    try {
      const latestValues = (await fetchResource('fieldValues', { configfieldid: selectedField.id }))
        .filter((value) => value.configFieldId === selectedField.id);
      const snapshotConflict = getFieldValueSnapshotConflict(baselineValues, latestValues);
      if (snapshotConflict) {
        setBulkValueValidation(snapshotConflict);
        return;
      }

      await mutateBatch('fieldValues', operations);
      setNotice(`${operations.filter((operation) => operation.method === 'POST').length} value(s) created and ${operations.filter((operation) => operation.method === 'PUT').length} value(s) updated successfully.`);
      clearPreviewCache();
      setBulkValueEditorOpen(false);
      refreshCoverage();
    } catch (requestError) {
      const progress = requestError.completedCount > 0
        ? ` ${requestError.completedCount} row(s) were saved before the error; close and reopen the bulk editor to review the latest server state.`
        : '';
      setBulkValueValidation(`${formatSalesApiError(requestError, 'Unable to save bulk values.')}${progress}`);
    } finally {
      setSaving(false);
    }
  };

  const saveBulkValueRules = async ({ rows, baselineRules }) => {
    if (!selectedField || !selectedStation) return;
    setSaving(true);
    setBulkRuleValidation('');
    setError('');
    try {
      const latestRules = await fetchResource('valueRules');
      const latestValues = await fetchResource('fieldValues');
      const latestAllowedValueIds = new Set(latestValues
        .filter((value) => value.configFieldId === selectedField.id)
        .map((value) => value.id));
      const latestScopedRules = getValueRuleBulkScopeRows(latestRules, selectedStation.id, latestAllowedValueIds);
      const snapshotConflict = getValueRuleSnapshotConflict(baselineRules, latestScopedRules);
      if (snapshotConflict) {
        setBulkRuleValidation(snapshotConflict);
        return;
      }

      const latestAnalysis = analyzeValueRuleBulkRows({
        rows,
        baselineRules,
        allRules: latestRules,
        stationId: selectedStation.id,
        fieldId: selectedField.id,
        fieldValues: latestValues,
      });
      if (latestAnalysis.errorCount > 0) {
        setBulkRuleValidation('The latest API data makes one or more staged rules invalid. Close and reopen the bulk editor, then review the highlighted rows.');
        return;
      }
      if (latestAnalysis.operations.length === 0) return;

      await mutateBatch('valueRules', latestAnalysis.operations);
      setNotice(`${latestAnalysis.createCount} value rule row(s) created and ${latestAnalysis.updateCount} row(s) updated successfully.`);
      clearPreviewCache();
      setBulkRuleEditorOpen(false);
      refreshCoverage();
    } catch (requestError) {
      const progress = requestError.completedCount > 0
        ? ` ${requestError.completedCount} row(s) were saved before the error; close and reopen the bulk editor to review the latest server state.`
        : '';
      setBulkRuleValidation(`${formatSalesApiError(requestError, 'Unable to save bulk value rules.')}${progress}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteRecord = async (resource, record, label) => {
    let deleteValidationData = data;
    try {
      if (resource === 'stations') {
        const stationSections = await fetchResource('sections', { stationid: record.id });
        const stationRules = await fetchResource('valueRules', { parentstationid: record.id });
        deleteValidationData = { ...data, sections: stationSections, valueRules: stationRules };
      } else if (resource === 'sections') {
        deleteValidationData = { ...data, subSections: await fetchResource('subSections', { sectionid: record.id }) };
      } else if (resource === 'subSections') {
        deleteValidationData = { ...data, fields: await fetchResource('fields', { subsectionid: record.id }) };
      } else if (resource === 'fields') {
        deleteValidationData = { ...data, fieldValues: await fetchResource('fieldValues', { configfieldid: record.id }) };
      } else if (resource === 'fieldValues') {
        const directRules = await fetchResource('valueRules', { fieldvalueid: record.id });
        const dependencyRules = await fetchResource('valueRules', { parentfieldvalueid: record.id });
        deleteValidationData = {
          ...data,
          valueRules: [...new Map([...directRules, ...dependencyRules].map((rule) => [rule.id, rule])).values()],
        };
      }
    } catch (requestError) {
      setError(formatSalesApiError(requestError, `Unable to verify whether ${label} can be deleted.`));
      return;
    }

    const blocker = findDeleteBlocker(resource, record, deleteValidationData);
    if (blocker) {
      setError(blocker);
      return;
    }
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    setError('');
    setNotice('');
    try {
      await mutate(resource, 'DELETE', { Id: record.id });
      if (resource === 'sections' && record.id === sectionId) setPath({}, ['section', 'subsection', 'field']);
      if (resource === 'subSections' && record.id === subSectionId) setPath({}, ['subsection', 'field']);
      if (resource === 'fields' && record.id === fieldId) setPath({}, ['field']);
      clearPreviewCache();
      setNotice(`${RESOURCE_LABELS[resource][0].toUpperCase()}${RESOURCE_LABELS[resource].slice(1)} deleted successfully.`);
      refreshCoverage();
    } catch (requestError) {
      setError(formatSalesApiError(requestError, `Unable to delete ${RESOURCE_LABELS[resource]}.`));
    }
  };

  const deleteRuleGroup = async (group) => {
    if (!window.confirm(`Delete rule group ${group.ruleGroupId} and all ${group.rows.length} of its condition rows? This cannot be undone.`)) return;
    setError('');
    setNotice('');
    try {
      await mutateBatch('valueRules', group.rows.map((rule) => ({ method: 'DELETE', payload: { Id: rule.id } })));
      clearPreviewCache();
      setNotice(`Rule group ${group.ruleGroupId} deleted successfully.`);
      refreshCoverage();
    } catch (requestError) {
      const progress = requestError.completedCount > 0 ? ` ${requestError.completedCount} row(s) were deleted before the error.` : '';
      setError(`${formatSalesApiError(requestError, `Unable to delete rule group ${group.ruleGroupId}.`)}${progress}`);
      try {
        await load();
      } catch {
        // Preserve the original delete error when recovery refresh fails.
      }
    }
  };

  const hierarchyBreadcrumbs = [
    { label: 'Configuration Builder', onClick: () => setPath({}, ['tab', 'station', 'section', 'subsection', 'field', 'view']) },
    ...(selectedStation ? [{ label: selectedStation.stationName, onClick: () => setPath({ station: selectedStation.id }, ['section', 'subsection', 'field', 'view']) }] : []),
    ...(!isStationChildView && selectedSection ? [{ label: selectedSection.label || selectedSection.sectionKey, onClick: () => setPath({ section: selectedSection.id }, ['subsection', 'field', 'view']) }] : []),
    ...(!isStationChildView && selectedSubSection ? [{ label: selectedSubSection.subSectionName, onClick: () => setPath({ subsection: selectedSubSection.id }, ['field', 'view']) }] : []),
    ...(!isStationChildView && selectedField ? [{ label: selectedField.fieldName }] : []),
    ...(isPartsView ? [{ label: 'Parts' }] : []),
    ...(isPartAutoSelectView ? [{ label: 'Part Auto Select' }] : []),
  ];

  const refreshPage = async () => {
    if (isPartsView) {
      setPartsRefreshVersion((current) => current + 1);
      return;
    }
    if (isPartAutoSelectView) {
      setPartAutoSelectRefreshVersion((current) => current + 1);
      return;
    }
    clearPreviewCache();
    await load();
    await refreshCoverage();
  };

  const renderPreviewStatus = (previewState, loadingText = 'Loading preview...') => {
    if (previewState?.status === 'loading') {
      return <div className="config-admin-preview-loading"><Spinner animation="border" size="sm" /> {loadingText}</div>;
    }
    if (previewState?.status === 'error') {
      return <Alert variant="warning">{previewState.error}</Alert>;
    }
    return null;
  };

  const renderSelectedStationPreview = (model, sectionOnly = false) => (
    <>
      {renderPreviewStatus(selectedPreviewState)}
      {selectedPreviewState?.status === 'ready' && (
        <ConfigurationPreviewPanel
          model={model}
          sourceData={selectedPreviewState.data}
          title={sectionOnly ? `${selectedSection.label || selectedSection.sectionKey} preview` : `${selectedStation.stationName} preview`}
          description={sectionOnly ? 'Interactive preview for this complete section using the current station rules.' : 'Interactive preview of all configured sections for this station.'}
          stationId={selectedStation.id}
          stationName={selectedStation.stationName}
          onValueChange={(previewFieldId, valueId) => updatePreviewSelection(stationId, previewFieldId, valueId)}
          onInputChange={(previewFieldId, value) => updatePreviewInput(stationId, previewFieldId, value)}
          pumpTableModel={selectedPumpTableModel}
        />
      )}
    </>
  );

  const renderStations = () => (
    <>
      <PageHeading
        title="Master Station List"
        description="Choose a station to configure its sections, subsections, fields, values, and rules."
        action={(
          <div className="config-admin-actions">
            <Button variant="outline-secondary" onClick={openStationTypePreview} disabled={stationPreviewOptions.length === 0}>
              <i className="bi bi-eye me-2" aria-hidden="true"></i>Preview
            </Button>
            <Button onClick={() => openCreate('stations')} disabled={!canEditSales}><i className="bi bi-plus-circle me-2"></i>Add station</Button>
          </div>
        )}
      />
      <Card><Card.Body className="p-0"><div className="table-responsive"><Table hover className="mb-0"><thead><tr><th>Order</th><th>Station</th><th>ID</th><th className="text-end">Actions</th></tr></thead><tbody>
        {stationPreviewOptions.map((station) => (
          <tr key={station.id}>
            <td>{station.sortOrder}</td>
            <td><button type="button" className="config-admin-row-link" onClick={() => selectStation(station.id)}>{station.stationName}<i className="bi bi-arrow-right"></i></button></td>
            <td>{station.id}</td>
            <td>
              <div className="config-admin-actions">
                <Button size="sm" variant="outline-secondary" onClick={() => openStationPreview(station)} aria-label={`Preview ${station.stationName}`}>
                  <i className="bi bi-eye" aria-hidden="true"></i> Preview
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => openPumpTablePreview(station)} aria-label={`Pump table preview for ${station.stationName}`}>
                  <i className="bi bi-grid-3x3-gap" aria-hidden="true"></i> Pump table
                </Button>
                <EditDeleteButtons label={station.stationName} disabled={!canEditSales} onOpen={() => selectStation(station.id)} onEdit={() => openEdit('stations', station)} onDelete={() => deleteRecord('stations', station, station.stationName)} />
              </div>
            </td>
          </tr>
        ))}
      </tbody></Table></div></Card.Body></Card>
    </>
  );

  const renderSectionList = () => (
    <>
      <PageHeading
        title={`Sections for ${selectedStation.stationName}`}
        description="Choose a section to continue. Global sections and sections assigned to this station are shown together."
        action={<Button onClick={() => openCreate('sections')} disabled={!canEditSales}><i className="bi bi-plus-circle me-2"></i>Add section</Button>}
      />
      {(coverage || coverageLoading) && (
        <div className="config-admin-coverage-overview">
          <div><span>Station rule coverage</span><strong>{selectedStation.stationName}</strong></div>
          {coverageLoading && !coverage ? <Spinner animation="border" size="sm" /> : <><span><strong>{coverage?.totals.activeSectionCount || 0}</strong> of {coverage?.totals.sectionCount || 0} sections active</span><span><strong>{coverage?.totals.activeFieldCount || 0}</strong> of {coverage?.totals.fieldCount || 0} fields active</span><span><strong>{coverage?.totals.coveredValueCount || 0}</strong> of {coverage?.totals.valueCount || 0} values covered</span></>}
        </div>
      )}
      {stationSections.length === 0 ? <EmptyState>No sections are available for this station.</EmptyState> : <Row className="g-3 config-admin-card-grid">{stationSections.map((section) => <Col key={section.id} xl={4} md={6}><Card className="config-admin-entity-card"><Card.Body><div><Badge bg={section.stationId === null ? 'secondary' : 'primary'}>{section.stationId === null ? 'All stations' : 'This station'}</Badge><button type="button" className="config-admin-card-link" onClick={() => selectSection(section.id)}>{section.label || section.sectionKey}<i className="bi bi-arrow-right"></i></button><p>{section.page} · Order {section.sortOrder}</p><CoverageStatus coverage={sectionCoverageById.get(section.id)} level="section" loading={coverageLoading} /></div>{section.stationId === null ? <span className="config-admin-shared-note"><i className="bi bi-lock me-1" aria-hidden="true"></i>Shared definition</span> : <EditDeleteButtons label={section.sectionKey} disabled={!canEditSales} onOpen={() => selectSection(section.id)} onEdit={() => openEdit('sections', section)} onDelete={() => deleteRecord('sections', section, section.sectionKey)} />}</Card.Body></Card></Col>)}</Row>}
      <section className="config-admin-detail-section" aria-label="Station section preview">
        {renderSelectedStationPreview(selectedStationPreviewModel)}
      </section>
    </>
  );

  const renderSubSectionList = () => (
    <>
      <PageHeading
        title={`Subsections in ${selectedSection.label || selectedSection.sectionKey}`}
        description="Choose a subsection to continue to its fields."
        action={<Button onClick={() => openCreate('subSections')} disabled={!canEditSales}><i className="bi bi-plus-circle me-2"></i>Add subsection</Button>}
      />
      {sectionSubSections.length === 0 ? <EmptyState>No subsections have been configured.</EmptyState> : <Card><Card.Body className="p-0"><div className="table-responsive"><Table hover className="mb-0"><thead><tr><th>Order</th><th>Subsection</th><th>Rule coverage</th><th className="text-end">Actions</th></tr></thead><tbody>{sectionSubSections.map((subSection) => <tr key={subSection.id}><td>{subSection.sortOrder}</td><td><button type="button" className="config-admin-row-link" onClick={() => selectSubSection(subSection.id)}>{subSection.subSectionName}<i className="bi bi-arrow-right"></i></button></td><td><CoverageStatus coverage={subSectionCoverageById.get(subSection.id)} level="subsection" loading={coverageLoading} /></td><td><EditDeleteButtons label={subSection.subSectionName} disabled={!canEditSales} onOpen={() => selectSubSection(subSection.id)} onEdit={() => openEdit('subSections', subSection)} onDelete={() => deleteRecord('subSections', subSection, subSection.subSectionName)} /></td></tr>)}</tbody></Table></div></Card.Body></Card>}
      <section className="config-admin-detail-section" aria-label="Selected section preview">
        {renderSelectedStationPreview(selectedSectionPreviewModel, true)}
      </section>
    </>
  );

  const renderFieldList = () => (
    <>
      <PageHeading
        title={`Fields in ${selectedSubSection.subSectionName}`}
        description="Choose a field to manage its possible values and station-specific rules."
        action={<Button onClick={() => openCreate('fields')} disabled={!canEditSales}><i className="bi bi-plus-circle me-2"></i>Add field</Button>}
      />
      {subSectionFields.length === 0 ? <EmptyState>No fields have been configured.</EmptyState> : <Card><Card.Body className="p-0"><div className="table-responsive"><Table hover className="mb-0"><thead><tr><th>Order</th><th>Field</th><th>Type</th><th>Scope</th><th>Rule coverage</th><th className="text-end">Actions</th></tr></thead><tbody>{subSectionFields.map((field) => <tr key={field.id}><td>{field.sortOrder}</td><td><button type="button" className="config-admin-row-link" onClick={() => selectField(field.id)}>{field.fieldName}<i className="bi bi-arrow-right"></i></button><small className="d-block text-muted">ID {field.id}</small></td><td><Badge bg="light" text="dark">{field.fieldType}</Badge></td><td><Badge bg={field.scope === 'Quote' ? 'info' : 'success'}>{field.scope}</Badge></td><td><CoverageStatus coverage={fieldCoverageById.get(field.id)} loading={coverageLoading} /></td><td><EditDeleteButtons label={field.fieldName} disabled={!canEditSales} onOpen={() => selectField(field.id)} onEdit={() => openEdit('fields', field)} onDelete={() => deleteRecord('fields', field, field.fieldName)} /></td></tr>)}</tbody></Table></div></Card.Body></Card>}
    </>
  );

  const renderFieldValues = () => (
    <>
      <PageHeading
        title={`${selectedField.fieldName}: possible values`}
        description="Define the reusable options for this field, then manage their station-specific behavior below."
        action={(
          <div className="d-flex flex-wrap gap-2">
            <Button variant="outline-primary" onClick={() => { setBulkValueValidation(''); setBulkValueEditorOpen(true); }} disabled={!canEditSales}>
              <i className="bi bi-table me-2" aria-hidden="true"></i>Bulk edit / upload
            </Button>
            <Button onClick={() => openCreate('fieldValues')} disabled={!canEditSales}><i className="bi bi-plus-circle me-2"></i>Add value</Button>
          </div>
        )}
      />
      <div className="config-admin-field-summary"><span><strong>Type</strong>{selectedField.fieldType}</span><span><strong>Scope</strong>{selectedField.scope}</span><span><strong>Order</strong>{selectedField.sortOrder}</span><span><strong>Field ID</strong>{selectedField.id}</span></div>
      <div className="config-admin-current-coverage"><span>Rule coverage for {selectedStation.stationName}</span><CoverageStatus coverage={fieldCoverageById.get(selectedField.id)} loading={coverageLoading} /></div>
      {fieldValues.length === 0 ? <EmptyState>This field has no defined values and is implicitly hidden for this station.</EmptyState> : <Card><Card.Body className="p-0"><div className="table-responsive"><Table hover className="mb-0"><thead><tr><th>{getFieldValueColumnLabel(selectedField.fieldType)}</th><th>Constraint</th><th>ID</th><th>General default</th><th className="text-end">Actions</th></tr></thead><tbody>{fieldValues.map((value) => { const valueLabel = getFieldValueLabel(value); const isRange = getFieldValueMode(value) === 'range'; const isHyperlink = isHyperlinkFieldType(selectedField.fieldType); const isButton = isButtonFieldType(selectedField.fieldType); return <tr key={value.id}><td><FieldValueDisplay field={selectedField} value={value} /></td><td><Badge bg={isRange || isHyperlink || isButton ? 'info' : 'light'} text={isRange || isHyperlink || isButton ? undefined : 'dark'}>{isRange ? 'Min / max' : isHyperlink ? 'Hyperlink' : isButton ? 'Button' : 'Fixed'}</Badge></td><td>{value.id}</td><td>{value.isDefaultValue ? <Badge bg="success">Default</Badge> : '—'}</td><td><EditDeleteButtons label={valueLabel} disabled={!canEditSales} onEdit={() => openEdit('fieldValues', value)} onDelete={() => deleteRecord('fieldValues', value, valueLabel)} /></td></tr>; })}</tbody></Table></div></Card.Body></Card>}
    </>
  );

  const renderRules = () => (
    <>
      <PageHeading
        title={`${selectedField.fieldName}: rules for ${selectedStation.stationName}`}
        description="Rules are grouped by Rule Group ID, then by allowed value. Each value starts with its station condition and may include AND dependencies; separate groups are evaluated as OR."
        action={(
          <div className="d-flex flex-wrap gap-2">
            <Button variant="outline-primary" onClick={openBulkValueRules} disabled={!canEditSales || fieldValues.length === 0}>
              <i className="bi bi-table me-2" aria-hidden="true"></i>Bulk edit / upload
            </Button>
            <Button onClick={() => openCreate('valueRules')} disabled={!canEditSales || fieldValues.length === 0}><i className="bi bi-plus-circle me-2"></i>Add rule group</Button>
          </div>
        )}
      />
      {fieldValues.length === 0 && <Alert variant="warning">Define possible values above before creating station rules.</Alert>}
      {fieldRuleGroups.length === 0 ? (
        <EmptyState>No rule groups exist for this station and field. The field is currently inferred as inactive.</EmptyState>
      ) : (
        <>
          <div className="config-admin-rule-toolbar">
            <Form.Group>
              <Form.Label>Search rules</Form.Label>
              <Form.Control
                type="search"
                placeholder="Rule group, allowed value, dependency field, or dependency value"
                value={ruleSearch}
                onChange={(event) => setRuleSearch(event.target.value)}
              />
              <Form.Text>
                {filteredFieldRuleGroups.length} matching {filteredFieldRuleGroups.length === 1 ? 'group' : 'groups'} · {filteredRuleValueCount} allowed {filteredRuleValueCount === 1 ? 'value' : 'values'}
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Rule status</Form.Label>
              <Form.Select value={ruleFilter} onChange={(event) => setRuleFilter(event.target.value)}>
                <option value="all">All rules</option>
                <option value="enabled">Enabled values</option>
                <option value="disabled">Disabled values</option>
                <option value="default">Default values</option>
                <option value="dependencies">Values with dependencies</option>
              </Form.Select>
            </Form.Group>
            {ruleDependencyFilterOptions.length > 0 && (
              <div className="config-admin-rule-dependency-filters">
                <div className="config-admin-rule-dependency-heading">
                  <div>
                    <strong>Dependency values</strong>
                    <small>Select values together to find an exact dependency combination.</small>
                  </div>
                  {Object.values(ruleDependencyFilters).some(Boolean) && (
                    <Button type="button" variant="link" size="sm" onClick={() => setRuleDependencyFilters({})}>
                      Clear dependency filters
                    </Button>
                  )}
                </div>
                <div className="config-admin-rule-dependency-grid">
                  {ruleDependencyFilterOptions.map((option) => (
                    <Form.Group key={option.fieldId}>
                      <Form.Label>{option.fieldName}</Form.Label>
                      <Form.Select
                        value={ruleDependencyFilters[option.fieldId] || ''}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setRuleDependencyFilters((current) => {
                            const next = { ...current };
                            if (nextValue) next[option.fieldId] = nextValue;
                            else delete next[option.fieldId];
                            return next;
                          });
                        }}
                      >
                        <option value="">Any {option.fieldName}</option>
                        {option.values.map((value) => (
                          <option key={value.id} value={value.id}>{getFieldValueLabel(value)}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  ))}
                </div>
              </div>
            )}
          </div>
          {filteredFieldRuleGroups.length === 0 ? (
            <EmptyState>No rule groups or allowed values match the current search and filter.</EmptyState>
          ) : (
            <div className="config-admin-rule-groups">
          {filteredFieldRuleGroups.map((group, groupIndex) => {
            const sourceGroup = fieldRuleGroups.find((candidate) => candidate.key === group.key) || group;
            return (
              <React.Fragment key={group.key}>
                {groupIndex > 0 && <div className="config-admin-auto-or-divider" aria-label="or rule group"><span>OR</span></div>}
                <Card className="config-admin-rule-group">
                  <Card.Header className="config-admin-rule-group-header config-admin-value-rule-group-header">
                    <div>
                      <h5>Rule group {group.ruleGroupId}</h5>
                      <small>
                        {group.values.length}{group.totalValueCount !== group.values.length ? ` of ${group.totalValueCount}` : ''} allowed {group.totalValueCount === 1 ? 'value' : 'values'} shown · {group.rows.length} matching API {group.rows.length === 1 ? 'row' : 'rows'}
                      </small>
                    </div>
                    <div className="config-admin-value-rule-actions">
                      <EditDeleteButtons
                        label={`rule group ${group.ruleGroupId}`}
                        disabled={!canEditSales}
                        onEdit={() => openEdit('valueRules', sourceGroup, { groupKey: sourceGroup.key })}
                        onDelete={() => deleteRuleGroup(sourceGroup)}
                      />
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div className="config-admin-group-values">
                      {group.values.map((valueBranch, valueIndex) => {
                        const value = data.fieldValues.find((item) => item.id === valueBranch.fieldValueId);
                        return (
                          <React.Fragment key={valueBranch.key}>
                            {valueIndex > 0 && <div className="config-admin-auto-or-divider config-admin-value-rule-or" aria-label="or allowed value"><span>OR</span></div>}
                            <div className="config-admin-value-rule-branch">
                              <div className="config-admin-value-rule-branch-header">
                                <div><small>Allowed value</small><h6>{value ? getFieldValueLabel(value) : `Value ${valueBranch.fieldValueId}`}</h6></div>
                                <div className="config-admin-rule-status">
                                  {valueBranch.isDefaultValue && <Badge bg="success">Default</Badge>}
                                  {valueBranch.isDisabled && <Badge bg="secondary">Disabled</Badge>}
                                  {!valueBranch.isDefaultValue && !valueBranch.isDisabled && <Badge bg="light" text="dark">Enabled</Badge>}
                                </div>
                              </div>
                              <div className="config-admin-auto-expression config-admin-value-rule-expression">
                                <span className="config-admin-auto-group">
                                  <span>Station is {selectedStation.stationName}</span>
                                {valueBranch.dependencies.map((dependency, index) => {
                                  const parentValue = ruleReferenceValues.find((item) => item.id === dependency.parentFieldValueId);
                                  const parentField = ruleReferenceFields.find((item) => item.id === parentValue?.configFieldId);
                                  return (
                                    <React.Fragment key={dependency.id}>
                                      <em>AND</em>
                                      <span>{parentField?.fieldName || `Field ${parentValue?.configFieldId || 'value'}`} is {parentValue ? getFieldValueLabel(parentValue) : `Value ${dependency.parentFieldValueId}`}</span>
                                    </React.Fragment>
                                  );
                                })}
                                </span>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </Card.Body>
                </Card>
              </React.Fragment>
            );
          })}
            </div>
          )}
        </>
      )}
    </>
  );

  const currentStep = isPartAutoSelectView ? 6 : isPartsView ? 5 : selectedField ? 4 : selectedSubSection ? 3 : selectedSection ? 2 : selectedStation ? 1 : 0;

  const renderHierarchy = () => {
    if (!selectedStation) return renderStations();
    if (isPartsView) return <StationPartsManager station={selectedStation} canEditSales={canEditSales} refreshVersion={partsRefreshVersion} />;
    if (isPartAutoSelectView) return <PartAutoSelectManager station={selectedStation} canEditSales={canEditSales} refreshVersion={partAutoSelectRefreshVersion} />;
    if (!selectedSection) return renderSectionList();
    if (!selectedSubSection) return renderSubSectionList();
    if (!selectedField) return renderFieldList();
    return (
      <>
        {renderFieldValues()}
        <section ref={ruleSectionRef} className="config-admin-detail-section" aria-label="Station value rules">
          {focusedBranchKeys && <Alert variant="info">
            <div>Showing applicable rules for {searchParams.get('pumpCell') || 'the selected pump table cell'}.</div>
            <div className="d-flex flex-wrap gap-2 mt-2">
              <Button size="sm" variant="outline-secondary" onClick={() => setPath({})}>Show all rules for this field</Button>
            </div>
          </Alert>}
          {renderRules()}
        </section>
      </>
    );
  };

  if (loading) return <section className="sales-portal-page config-admin-page"><div className="sales-loading"><Spinner animation="border" size="sm" /> Loading configuration administration...</div></section>;

  return (
    <section className="sales-portal-page config-admin-page" aria-labelledby="configuration-admin-title">
      <div className="config-admin-title-block"><div><span className="config-admin-kicker">Sales Portal Administration</span><h2 id="configuration-admin-title">Configuration Builder</h2><p>Build station configuration data without changing frontend code.</p></div><div className="d-flex gap-2"><Button variant="outline-primary" onClick={() => setGlobalRulesOpen(true)}>Global Rules</Button><Button variant="outline-primary" onClick={refreshPage}><i className="bi bi-arrow-clockwise me-2"></i>Refresh</Button></div></div>
      <div className="mb-3"><Button variant="outline-primary" onClick={() => setPricingFormulasOpen(true)}>Pricing Formulas</Button></div>
      {pricingFormulasOpen && <PricingFormulasManager onHide={() => { setPricingFormulasOpen(false); clearPreviewCache(); }} canEditSales={canEditSales} stations={data.stations} />}
      {globalRulesOpen && <GlobalRulesManager onHide={() => { setGlobalRulesOpen(false); clearPreviewCache(); }} canEditSales={canEditSales} stations={data.stations} />}

      <HierarchyProgress
        currentStep={currentStep}
        stationChildEnabled={Boolean(selectedStation)}
        selectedView={searchParams.get('view')}
        onSelectStationChild={selectStationChild}
      />
      <AdminBreadcrumbs items={hierarchyBreadcrumbs} />
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {coverageError && <Alert variant="warning">Coverage summary is unavailable: {coverageError}</Alert>}
      {notice && <Alert variant="success" dismissible onClose={() => setNotice('')}>{notice}</Alert>}

      <div className="config-admin-content">
        {renderHierarchy()}
      </div>


      <FieldValueBulkEditor
        show={bulkValueEditorOpen}
        field={selectedField}
        values={fieldValues}
        saving={saving}
        validation={bulkValueValidation}
        onClose={() => { setBulkValueEditorOpen(false); setBulkValueValidation(''); }}
        onSave={saveBulkFieldValues}
      />
      <ValueRuleBulkEditor
        show={bulkRuleEditorOpen}
        station={selectedStation}
        field={selectedField}
        fieldValues={fieldValues}
        baselineRules={fieldRuleRows}
        allRules={data.valueRules}
        referenceFields={ruleReferenceFields}
        referenceValues={ruleReferenceValues}
        saving={saving}
        validation={bulkRuleValidation}
        onClose={() => { setBulkRuleEditorOpen(false); setBulkRuleValidation(''); }}
        onSave={saveBulkValueRules}
      />
      <Modal show={Boolean(previewModal)} enforceFocus={!editor} onHide={editor ? undefined : closeStationPreview} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {previewModal?.allowStationSelect
              ? 'Station type preview'
              : previewModalStation ? `${previewModalStation.stationName} preview` : 'Station preview'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderPreviewStatus(previewModalState, 'Loading station preview...')}
          {previewModalState?.status === 'ready' && (
            <ConfigurationPreviewPanel
              model={previewModalModel}
              sourceData={previewModalState.data}
              title={previewModalStation ? `${previewModalStation.stationName} configuration` : 'Configuration preview'}
              description={previewModal?.allowStationSelect ? 'Choose a station type to preview its configured form. Changes here update preview state and are never saved.' : 'Interactive preview only. Changes here update preview state and are never saved.'}
              stationId={previewModalStation?.id}
              stationName={previewModalStation?.stationName}
              stationOptions={stationPreviewOptions}
              stationSelectable={Boolean(previewModal?.allowStationSelect)}
              onStationChange={changePreviewModalStation}
              onValueChange={(previewFieldId, valueId) => updatePreviewSelection(previewModal.stationId, previewFieldId, valueId)}
              onInputChange={(previewFieldId, value) => updatePreviewInput(previewModal.stationId, previewFieldId, value)}
              autoPickState={previewAutoPickState}
              autoPickModel={previewAutoPickModel}
              onAutoPick={runPreviewAutoPick}
              pumpTableModel={previewModalPumpTableModel}
              onPumpCellOpen={canEditSales ? (cell, label) => openPumpCellRules(cell, label, previewModal.stationId) : undefined}
              onPumpRowsChange={updatePreviewPumpRows}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeStationPreview}>Close</Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={Boolean(pumpTableModal)}
        enforceFocus={!editor && !pumpAssignmentView}
        onHide={editor ? undefined : closePumpTablePreview}
        size="xl"
        dialogClassName="config-admin-pump-table-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>{pumpTableStation ? `${pumpTableStation.stationName} pump table preview` : 'Pump table preview'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-3" role="group" aria-label="Pump table view">
            <Button variant={!pumpAssignmentView ? 'primary' : 'outline-primary'} onClick={() => setPumpAssignmentView(false)}>Configuration / HP</Button>
            <Button variant={pumpAssignmentView ? 'primary' : 'outline-primary'} onClick={() => setPumpAssignmentView(true)}>AutoPick pump assignments</Button>
          </div>
          {renderPreviewStatus(pumpTableState, 'Loading pump table rules...')}
          {pumpTableState?.status === 'ready' && pumpTableModel && !pumpAssignmentView && (
            <PumpTablePreview model={pumpTableModel} stationName={pumpTableStation?.stationName || 'Station'}
              onCellOpen={canEditSales ? (cell, label) => openPumpCellRules(cell, label, pumpTableModal.stationId) : undefined} />
          )}
          {pumpTableModal && pumpAssignmentView && pumpTableState?.status === 'ready' && pumpTableStation && (
            <PumpAssignmentMatrix key={pumpTableStation.id} station={pumpTableStation} source={pumpTableState.data}
              overviewModel={pumpTableModel} Matrix={PumpTablePreview} canEdit={canEditSales} onChanged={() => setAutoPickPreviewCache({})} />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePumpTablePreview}>Close</Button>
        </Modal.Footer>
      </Modal>
      <EditorModal editor={editor} data={editor?.context?.editorData || data} onClose={() => setEditor(null)} onChange={(draft) => setEditor((current) => ({ ...current, draft, dirty: true, savedMessage: '' }))} onSave={saveEditor} saving={saving} validation={validation}>
        {editor?.context?.pump && <>
          <p className="text-muted">{editor.context.pump.label}</p>
          {editor.context.copiedGroupId && <Alert variant="info">
            Copied group {editor.context.copiedGroupId} into a new draft. Adjust its conditions, then select Create rule group to save. The copy is not marked as a default.
          </Alert>}
          <PumpCellRuleOverview pump={editor.context.pump} fieldId={editor.context.fieldId} groupKey={editor.context.groupKey} saving={saving} onSelect={(option) => {
            if (editor.dirty && !window.confirm('Discard unsaved rule edits and open another group?')) return;
            openPumpRuleOption(option, editor.context.pump);
          }} onCopy={(option) => copyPumpRuleOption(option, editor.context.pump)} onDelete={canEditSales ? (option) => deletePumpRuleOption(option, editor.context.pump) : undefined} />
        </>}
      </EditorModal>
    </section>
  );
};

const ConfigurationAdmin = () => {
  const { canEdit } = usePermissions();
  return <ConfigurationAdminContent canEditSales={canEdit('sales.container')} />;
};

export default ConfigurationAdmin;
